import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useMarketTheme } from '@/hooks/useMarketTheme';
import { useUserLimits } from '@/hooks/useUserLimits';
import { showUndoToast } from '@/lib/undoToast';
import { useLocation, useNavigate } from 'react-router-dom';
import StockTable from '@/components/StockTable';
import WatchlistLeverageCallout from '@/components/WatchlistLeverageCallout';
import { useTrendingWatchlistTickers } from '@/components/WatchlistRadarChips';
import BadgeKey from '@/components/BadgeKey';
import StreakHighlightRow from '@/components/StreakHighlightRow';
import LivePriceIndicator from '@/components/LivePriceIndicator';
import usePriceStream from '@/hooks/usePriceStream';
import useDebouncedSetter from '@/hooks/useDebouncedSetter';
import StreakAtRiskRow from '@/components/StreakAtRiskRow';
import { HeatmapTop3Strip } from '@/components/HeatmapTop3Strip';
import TierCutsRow from '@/components/TierCutsRow';
import AdminDividendDatesButton from '@/components/AdminDividendDatesButton';
import AdminQualityRebackfillButton from '@/components/AdminQualityRebackfillButton';
import AdminSqueezeRadarButton from '@/components/AdminSqueezeRadarButton';
import AdminDividendGateAuditButton from '@/components/AdminDividendGateAuditButton';
import AdminSectorBackfillButton from '@/components/AdminSectorBackfillButton';
import AdminPendingDividendReviewButton from '@/components/AdminPendingDividendReviewButton';
import AdminCheckRecycledTickerButton from '@/components/AdminCheckRecycledTickerButton';
import AdminPreferredTermsButton from '@/components/AdminPreferredTermsButton';
import PortfolioCallExposureBanner from '@/components/PortfolioCallExposureBanner';
import AdminExpiredWarrantsButton from '@/components/AdminExpiredWarrantsButton';
import AdminRecycledLockedPanel from '@/components/AdminRecycledLockedPanel';
import AdminPerfRefreshSkippedPanel from '@/components/AdminPerfRefreshSkippedPanel';
import AdminSlowRequestsButton from '@/components/AdminSlowRequestsButton';
import AdminSpacCleanupButton from '@/components/AdminSpacCleanupButton';
import AdminRecoverMissingTickersButton from '@/components/AdminRecoverMissingTickersButton';
import AdminTagBabyBondsButton from '@/components/AdminTagBabyBondsButton';
import AdminDataProvenanceHealthButton from '@/components/AdminDataProvenanceHealthButton';
import AdminHydrateNonDividendUniverseButton from '@/components/AdminHydrateNonDividendUniverseButton';
import AdminPendingNonDividendReviewButton from '@/components/AdminPendingNonDividendReviewButton';
import AdminDivToNonDivButton from '@/components/AdminDivToNonDivButton';
import AdminNonDivToDivButton from '@/components/AdminNonDivToDivButton';
import AdminRefreshFamousHoldersButton from '@/components/AdminRefreshFamousHoldersButton';
import AdminScrubStructuralNonDivButton from '@/components/AdminScrubStructuralNonDivButton';
import AdminDeleteDefaultAdminButton from '@/components/AdminDeleteDefaultAdminButton';
import AdminScoreNonDivQualityButton from '@/components/AdminScoreNonDivQualityButton';
import AdminNonDivPerfWarmerButton from '@/components/AdminNonDivPerfWarmerButton';
import DividendFilterSegmentedControl from '@/components/DividendFilterSegmentedControl';
import WhatChangedBanner from '@/components/WhatChangedBanner';import AdminWarmFinancialHealthButton from '@/components/AdminWarmFinancialHealthButton';
import AdminWarmYieldHistoryButton from '@/components/AdminWarmYieldHistoryButton';
import ExDivAlertModal from '@/components/ExDivAlertModal';
import IpoAlertModal from '@/components/IpoAlertModal';
import DividendPaymentAlertModal from '@/components/DividendPaymentAlertModal';
import ExDivAlertSettings from '@/components/ExDivAlertSettings';
import Stage2AlertSettings from '@/components/Stage2AlertSettings';
import AdminCronHistoryDropdown from '@/components/AdminCronHistoryDropdown';
import AdminRebuildStreaksButton from '@/components/AdminRebuildStreaksButton';
import AdminResyncTiersButton from '@/components/AdminResyncTiersButton';
import AdminReconcileTierCutsButton from '@/components/AdminReconcileTierCutsButton';
import AdminAutoReconcileForeignAdrsButton from '@/components/AdminAutoReconcileForeignAdrsButton';
import TierResyncStatusBadge from '@/components/TierResyncStatusBadge';
import AdminDataHygieneButton from '@/components/AdminDataHygieneButton';
import AdminCommunityButton from '@/components/AdminCommunityButton';
import AdminMmfSeedButton from '@/components/AdminMmfSeedButton';
import AdminMassiveApiWatchCard from '@/components/AdminMassiveApiWatchCard';
import AdminHowToFeedbackButton from '@/components/AdminHowToFeedbackButton';
import AdminLearnGuideFeedbackButton from '@/components/AdminLearnGuideFeedbackButton';
import AdminBlocklistTickerButton from '@/components/AdminBlocklistTickerButton';
import AdminMarkLiquidatedButton from '@/components/AdminMarkLiquidatedButton';
import AdminHydrateBenchmarkEtfsButton from '@/components/AdminHydrateBenchmarkEtfsButton';
import AdminButtonsCollapse from '@/components/AdminButtonsCollapse';
import AdminTreasuryClassifyButton from '@/components/AdminTreasuryClassifyButton';
import AdminButtonGroup from '@/components/AdminButtonGroup';
import AdminWarmerButton from '@/components/AdminWarmerButton';
import AdminYahooTouchPill from '@/components/AdminYahooTouchPill';
import AdminUniverseHealthPill from '@/components/AdminUniverseHealthPill';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import { useReopenModalOnMount } from '@/lib/stockModalRestore';
import StockModal from '@/components/StockModal';
import RecentCorpActionsPanel from '@/components/RecentCorpActionsPanel';import LoginModal from '@/components/LoginModal';
import PortfolioView from '@/components/PortfolioView';
import PaycheckStrip from '@/components/PaycheckStrip';
import PaydayPulseCards from '@/components/PaydayPulseCards';
import PaidThisMonthCard from '@/components/PaidThisMonthCard';
import IncomeHeatmap from '@/components/IncomeHeatmap';
import PersonalStreaksCard from '@/components/PersonalStreaksCard';
import WatchlistNews, { TickerNewsBlock } from '@/components/WatchlistNews';
import StarredFilingsStrip from '@/components/StarredFilingsStrip';
import WarrantAlertStrip from '@/components/WarrantAlertStrip';
import * as starredStore from '@/lib/starredFilingsStore';
import WatchlistSummaryCard from '@/components/WatchlistSummaryCard';
import WatchlistSelector from '@/components/WatchlistSelector';
import UnusualActivityLine from '@/components/UnusualActivityLine';
import RecentDividendEventsLine from '@/components/RecentDividendEventsLine';
import ExDividendThisWeekLine from '@/components/ExDividendThisWeekLine';
import PortfolioHealthSnapshot from '@/components/PortfolioHealthSnapshot';
import WatchlistTimeMachineBar from '@/components/WatchlistTimeMachineBar';
import TopMoversWidget from '@/components/TopMoversWidget';
import CapitalAnalyticsLeaderboard from '@/components/CapitalAnalyticsLeaderboard';
import Stage2Leaderboard from '@/components/Stage2Leaderboard';
import DashboardSection from '@/components/DashboardSection';
import { getOwnedTickers } from '@/lib/bootRequests';
import CompareQualityModal from '@/components/CompareQualityModal';
import CompareThesesModal from '@/components/CompareThesesModal';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import PortfolioCharts from '@/components/PortfolioCharts';
import PortfolioSummary, { BenchmarkComparison } from '@/components/PortfolioSummary';
import WarrantExposureCard from '@/components/WarrantExposureCard';
import ConvictionSnapshotCard from '@/components/ConvictionSnapshotCard';
import PortfolioRiskBanners from '@/components/PortfolioRiskBanners';
import { useActivePortfolioId } from '@/components/PortfolioSelector';
import { getSnapshot as getPortfolioStoreSnapshot } from '@/lib/portfolioStore';
import CreatePortfolioModal from '@/components/CreatePortfolioModal';
import { useIsReadOnlyAllView } from '@/lib/useIsReadOnlyAllView';
import PortfolioDripModal from '@/components/PortfolioDripModal';
import SharePortfolioModal from '@/components/SharePortfolioModal';
import ToolBoxModal from '@/components/ToolBoxModal';
import CsvImportModal from '@/components/CsvImportModal';
import JournalEntryModal from '@/components/JournalEntryModal';
import { showDivChangeToast } from '@/components/DivChangeToast';
import TaxHarvestBanner from '@/components/TaxHarvestBanner';
import NiitYearEndBanner from '@/components/NiitYearEndBanner';
import NiitOnboardingNudge from '@/components/NiitOnboardingNudge';
import BrokerTaggingNudge from '@/components/BrokerTaggingNudge';
import DividendCalendar from '@/components/DividendCalendar';
import AlertsPanel, { AlertsBadge } from '@/components/AlertsPanel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Search, Briefcase, TrendingUp, LogOut, LogIn, Sun, Moon, Monitor, Activity, Star, ArrowLeft, Newspaper, Download, Upload, Filter, ChevronDown, Eraser, AlertTriangle, X, Clock, Receipt, BarChart3, BellRing, Crown, Landmark } from 'lucide-react';
import { smoothScrollToEl } from '@/lib/smoothScroll';

// Pagination scroll-back: after Next/Prev, climb smoothly to the
// search bar (medium-fast, not instant) so the user reads the new
// page top-down instead of being stranded at the bottom.
function scrollToTableTop(selector) {
  smoothScrollToEl(document.querySelector(selector));
}
import CompareModal from '@/components/CompareModal';
import PinnedPairsRow from '@/components/PinnedPairsRow';
import PortfolioBlendCompareCard from '@/components/PortfolioBlendCompareCard';
import MacroContextStrip from '@/components/MacroContextStrip';
import MoversNewsStrip from '@/components/MoversNewsStrip';
import LateCycleBanner from '@/components/LateCycleBanner';
import SplitAlertBanner from '@/components/SplitAlertBanner';
import SqueezeRadarStrip from '@/components/SqueezeRadarStrip';
import BreakawayStrip from '@/components/BreakawayStrip';
import CoverageRadarStrip from '@/components/CoverageRadarStrip';
import JoinCommunityCard from '@/components/JoinCommunityCard';
import { COMMUNITY_ENABLED } from '@/config/featureFlags';
import MyPortfolioCommunityPosts from '@/components/MyPortfolioCommunityPosts';
import { Link } from 'react-router-dom';
import { fetchStockWithFallback } from '@/lib/stockFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const downloadCSV = async (endpoint, filename) => {
  try {
    const response = await axios.get(`${API}/${endpoint}`, {
      withCredentials: true,
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch {}
};

export default function Dashboard() {
  const { isDark, preference, setThemePreference } = useMarketTheme();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  // Aug 2026 — "Search Stocks" sticky mode. When the admin clicks
  // the Search Stocks button in the header (or clears the input
  // while in this mode), we set this to true so the "hide
  // everything above the leaderboard" behavior stays on even with
  // an empty input. Unmounted (=reset) when the user navigates
  // away from the Dashboard, refreshes the browser, or clicks the
  // DiviDrip / Twylight Crow title. Stock modal, calculator,
  // compare modal, glossary tab, and new-tab news links do NOT
  // reset this (they don't unmount Dashboard).
  const [searchStickyMode, setSearchStickyMode] = useState(false);
  const listAbortRef = useRef(null);
  // Feb 2026 — Recent Searches. Persists the last 5 tickers the
  // user opened via `handleStockClick`. Rendered as tappable
  // chips just below the search box so returning to a name
  // they were just looking at is one tap away. localStorage-
  // backed so the list survives refresh + tab close.
  const RECENT_SEARCH_KEY = 'dividrip:recent-searches';
  const RECENT_SEARCH_MAX = 4;
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      const raw = localStorage.getItem(RECENT_SEARCH_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.slice(0, RECENT_SEARCH_MAX) : [];
    } catch { return []; }
  });
  const pushRecentSearch = useCallback((ticker) => {
    if (!ticker) return;
    const t = String(ticker).toUpperCase();
    setRecentSearches((prev) => {
      const next = [t, ...prev.filter((x) => x !== t)].slice(0, RECENT_SEARCH_MAX);
      try { localStorage.setItem(RECENT_SEARCH_KEY, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, []);
  // Round 92o++++++++N++++++++++ (May 2026) — local-input mirror for
  // the debounced search. The Input's `value` binds to this so
  // typed characters appear INSTANTLY, but the downstream
  // `searchQuery` state (which drives filtering, screener navigation,
  // etc.) only updates 1.5s after typing stops. This stops the
  // "typing too fast → wrong results / scrambled list" bug the
  // user reported without touching the carefully-tuned search
  // matching logic itself.
  const [searchInput, setSearchInput] = useState('');
  // Debounced setter — typing fires `setSearchInput` immediately so
  // the field stays responsive, then `setSearchQuery` 1.5s after
  // the user stops typing.
  const debouncedSetSearchQuery = useDebouncedSetter(setSearchQuery, 1000);
  const [stocks, setStocks] = useState([]);
  const [filteredStocks, setFilteredStocks] = useState([]);
  const [allTickers, setAllTickers] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [ownedTickers, setOwnedTickers] = useState([]);
  // Round 95ad (Jun 2026) — watchlist-scoped Buy Checklist rollup,
  // mirrors per-portfolio snapshot. Only fetched when the user
  // switches to viewMode='watchlist' to keep the dashboard load
  // light. Refreshes when the watchlist changes length (proxy for
  // add/remove events).
  const [watchlistHealthSnapshot, setWatchlistHealthSnapshot] = useState(null);
  const userLimits = useUserLimits();
  const [selectedStock, setSelectedStock] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [emptyPortfolioModal, setEmptyPortfolioModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchPage, setSearchPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalStocks, setTotalStocks] = useState(0);
  // Round 104d (Jun 2026) — Sort ALWAYS defaults to Name ascending
  // (numbers first, then A→Z), per user spec. The old Round 96j
  // localStorage persistence made a stray Price-header click stick
  // forever across reloads AND filter switches (user report: switching
  // Non-Dividend → Dividend kept showing price-asc). Removed — header
  // clicks still sort within the session, they just don't persist.
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Round P144 (Feb 2026) — "Allow SPAC & Warrant" checkbox. Default
  // OFF: main stock table + screener hide blank-check shells and
  // warrant tickers so a "price asc" sort surfaces real low-priced
  // stocks instead of a wall of $0.00 SPAC units. Sticks per browser
  // session via sessionStorage so navigating between Dashboard tabs
  // preserves the choice for the current visit, then resets on the
  // next visit. Watchlist / Portfolio views ignore this flag — they
  // always show every holding.
  const [allowSpacsWarrants, setAllowSpacsWarrants] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return sessionStorage.getItem('dividrip.allowSpacsWarrants') === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem('dividrip.allowSpacsWarrants', allowSpacsWarrants ? '1' : '0');
    } catch { /* ignore */ }
  }, [allowSpacsWarrants]);
  // Sep 2026 — "Allow: Money Market Funds" (session-scoped, default OFF).
  const [allowMmf, setAllowMmf] = useState(() => {
    try { return sessionStorage.getItem('dividrip.allowMmf') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { sessionStorage.setItem('dividrip.allowMmf', allowMmf ? '1' : '0'); } catch { /* ignore */ }
  }, [allowMmf]);

  // Round 95z — Section 4: 3-way segmented control filter
  // (Dividend / All / Non-Dividend). Sticky in URL so reloads + back-
  // button restore the filter the admin had selected. Default
  // "dividend" matches pre-expansion behaviour for every non-admin
  // user. URL param is the source of truth — the state below mirrors
  // it so the segmented control highlights the right pill.
  const [dividendFilter, setDividendFilter] = useState(() => {
    if (typeof window === 'undefined') return 'dividend';
    const p = new URLSearchParams(window.location.search).get('filter');
    return p === 'all' || p === 'non_dividend' ? p : 'dividend';
  });

  // Round 84k Phase C scaffolding (Feb 15, 2026) — subscribe to live
  // prices ONLY for the 25 stocks currently visible in the table.
  // Mirrors the exact slice logic the StockTable receives below:
  //   - If a search query is active in 'all' view: slice the filtered
  //     results to the current `searchPage` window.
  //   - Otherwise: `filteredStocks` IS the 25 from the backend page
  //     (server-side pagination already produced one page worth).
  // Re-subscription triggers automatically on:
  //   * search query change (filteredStocks rebuilds)
  //   * pagination Previous/Next (page/searchPage state change)
  //   * sort change (server refetches → filteredStocks rebuilds)
  //   * view switch (portfolio/watchlist/all → fetches new 25)
  // The memo key is the ticker list itself, so identical lists across
  // re-renders are de-duped inside `usePriceStream` to keep the SSE
  // connection alive without tearing down.
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [dataStatus, setDataStatus] = useState(null);
  const [prefetchedNews, setPrefetchedNews] = useState(null);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [initialTab, setInitialTab] = useState('dividend-info');
  const [initialChartPeriod, setInitialChartPeriod] = useState(null);
  // Round 92o++++++++N++++++++ — Deep-link accession passed through
  // to StockModal → TenK8KRisksTab so the bell-click flow can scroll
  // to and pulse a specific 8-K row.
  const [highlightFiling, setHighlightFiling] = useState(null);
  // Initialize viewMode from URL so we never flash the wrong view
  const initView = new URLSearchParams(location.search).get('view') || 'all';
  const [viewMode, setViewMode] = useState(initView === 'calendar' ? 'portfolio' : initView);
  // Round 86c (May 2026) — Trending watchlist tickers. Only fetched
  // when the user is on the watchlist view (hook short-circuits when
  // `enabled` is false). Returned as a Set the row renderer checks.
  const trendingWatchlistTickers = useTrendingWatchlistTickers(viewMode === 'watchlist');

  // Round 95ad — fetch the Health Snapshot only when the user is
  // actually on the watchlist view. Lightweight cache TTL on the
  // backend keeps repeat visits fast.
  useEffect(() => {
    if (viewMode !== 'watchlist' || !user) {
      setWatchlistHealthSnapshot(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/watchlist/health-snapshot`, { withCredentials: true })
      .then((r) => {
        if (!cancelled) setWatchlistHealthSnapshot(r?.data?.snapshot || null);
      })
      .catch(() => {
        if (!cancelled) setWatchlistHealthSnapshot(null);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, user?.user_id, watchlist.length]);

  // (memo MUST come AFTER all the state above — temporal dead zone)
  const dashboardSubTickers = useMemo(() => {
    const isSearchSlice = searchQuery.trim() && viewMode === 'all';
    const visible = isSearchSlice
      ? filteredStocks.slice((searchPage - 1) * 25, searchPage * 25)
      : filteredStocks;
    return visible.map((s) => String(s?.ticker || '').toUpperCase()).filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filteredStocks.map((s) => s?.ticker).join(','),
    searchQuery,
    searchPage,
    viewMode,
  ]);
  const { status: dashboardStreamStatus } = usePriceStream(dashboardSubTickers);
  const [chartsRefreshKey, setChartsRefreshKey] = useState(0);
  const [showNews, setShowNews] = useState(false);
  const [showWatchlistNews, setShowWatchlistNews] = useState(false);
  // Round 93r — Watchlist ex-div alert settings disclosure.
  const [showWatchlistAlertSettings, setShowWatchlistAlertSettings] = useState(false);
  // Round 98o (Jul 2026) — Portfolio Alerts button lives inside
  // <PortfolioSummary> already, so we no longer need a duplicate
  // Dashboard-level toggle. This state is kept commented for
  // reference in case we need a dashboard-level Portfolio Alerts
  // pill later (e.g., for mobile portrait where PortfolioSummary
  // isn't visible above the fold).
  // const [showPortfolioAlertSettings, setShowPortfolioAlertSettings] = useState(false);
  // Time Machine toggle — when ON, every watchlist row gets an
  // orange-outlined "$1000 1 year ago with DRIP" simulation bar
  // appended via the StockTable's `interleaveAfterRow` slot.
  const [showTimeMachine, setShowTimeMachine] = useState(false);
  const [showCalendar, setShowCalendar] = useState(initView === 'calendar');
  const [viewLoading, setViewLoading] = useState(false);
  const [showDripModal, setShowDripModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showCsvImportModal, setShowCsvImportModal] = useState(false);
  // Round P108f (Feb 2026) — user-requested guard. If a Plus user is
  // on the "All Portfolios" combined view (dashActivePortfolioId ==
  // null), the CSV importer has no real target to land rows into —
  // "All Portfolios" is a read-only combined view, not a portfolio
  // of its own. Historically this fell through to backend
  // portfolio_id=null and (a) landed CSV rows unassigned, and (b)
  // broke the undo snapshot / restore (iter 123 P0). The Import CSV
  // button now redirects to the "Name Portfolio" modal in this case
  // — the user names a portfolio first, and CreatePortfolioModal
  // hands off to its own inline CsvImportModal with the fresh
  // portfolio_id plumbed through. Non-Plus / already-scoped flows
  // are unchanged.
  const [showCreatePortfolioForCsv, setShowCreatePortfolioForCsv] = useState(false);
  // Round 92o++++++++N++++++++++ (May 2026) — Subscribe to the
  // active-portfolio store so the Dashboard's Tool Box CSV importer
  // can plumb `portfolioId` through to the backend dupe-check (and
  // to the lot-tagging on insert). Without this, the Tool Box
  // launcher sent portfolio_id=null and the backend's fallback
  // landed on whatever was active on the user doc — fine for
  // dupe-scoping, but the active-id wasn't surfaced in the modal's
  // "Importing into:" banner, so the user couldn't see which
  // portfolio they were targeting.
  const { activeId: dashActivePortfolioId } = useActivePortfolioId();
  // Round 92o++++++++N++++++++++ (Jun 2026) — hide destructive
  // "Reset and Delete Portfolio" button on the Plus-tier All view
  // because there's no portfolio there to wipe; the button used to
  // call `/portfolio/reset-all` which nukes EVERY portfolio at once
  // — a sharp footgun for users mid-audit who just want a single
  // portfolio gone.
  const { isReadOnly: isAllReadOnly } = useIsReadOnlyAllView();
  // Round 86c (May 2026) — Tool Box modal: single launcher for every
  // portfolio-page tool. Dispatched via the `open-toolbox-modal`
  // window event by the launcher button in PortfolioSummary's My
  // Portfolio card (desktop) and the mobile search row (phone).
  const [showToolbox, setShowToolbox] = useState(false);
  const [compareInitialTicker, setCompareInitialTicker] = useState(null);
  // When a user clicks a ticker inside CompareModal, we save the
  // {a, b} snapshot here so the StockModal can offer a "← Back to
  // Compare" button that restores both slots exactly as they were.
  const [compareSnapshot, setCompareSnapshot] = useState(null);
  const [showClearWatchlistDialog, setShowClearWatchlistDialog] = useState(false);
  const [clearingWatchlist, setClearingWatchlist] = useState(false);
  const [showCompareQuality, setShowCompareQuality] = useState(false);
  const [showCompareTheses, setShowCompareTheses] = useState(false);
  const [showResetPortfolioDialog, setShowResetPortfolioDialog] = useState(false);
  const [resettingPortfolio, setResettingPortfolio] = useState(false);
  // Round P118 (Feb 2026) — Read the active portfolio's name from the
  // store so the reset-confirmation dialog can explicitly label WHICH
  // portfolio is about to be wiped. Fixes the ambiguous "your
  // portfolio" copy that let a Plus user click Reset while viewing
  // Test 1 and get every sibling portfolio wiped.
  const [resetTargetPortfolioName, setResetTargetPortfolioName] = useState(null);
  useEffect(() => {
    if (!showResetPortfolioDialog) return;
    const snap = getPortfolioStoreSnapshot();
    const activePid = dashActivePortfolioId || snap?.activeId || null;
    if (activePid && Array.isArray(snap?.portfolios)) {
      const match = snap.portfolios.find((p) => p.portfolio_id === activePid);
      setResetTargetPortfolioName(match?.name || null);
    } else {
      setResetTargetPortfolioName(null);
    }
  }, [showResetPortfolioDialog, dashActivePortfolioId]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [alertsCount, setAlertsCount] = useState(0);
  const [alertsTriggered, setAlertsTriggered] = useState(0);

  // PortfolioSummary renders the Share button inside its title row — it
  // fires this window event and we open the same SharePortfolioModal here.
  useEffect(() => {
    const handler = () => setShowShareModal(true);
    window.addEventListener('open-share-portfolio-modal', handler);
    return () => window.removeEventListener('open-share-portfolio-modal', handler);
  }, []);

  // Tool Box launcher event — same pattern as the share modal.
  useEffect(() => {
    const handler = () => setShowToolbox(true);
    window.addEventListener('open-toolbox-modal', handler);
    return () => window.removeEventListener('open-toolbox-modal', handler);
  }, []);

  // Round 95p·2 (Feb 2026) — Reopen login modal when the user
  // navigates back from the ForgotPassword page via its "Back to
  // sign in" link. ForgotPassword sets `open_login_modal=1` in
  // sessionStorage right before navigating home; we consume + clear
  // the flag on mount so this fires exactly once and only when the
  // user isn't already signed in.
  useEffect(() => {
    let flag = null;
    try { flag = sessionStorage.getItem('open_login_modal'); } catch { /* noop */ }
    if (flag === '1' && !user) {
      try { sessionStorage.removeItem('open_login_modal'); } catch { /* noop */ }
      setLoginModalOpen(true);
    }
    // Only on initial mount — subsequent user-state changes shouldn't retrigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Round 97l (Feb 2026) — Auto-resume "Pin Compare" after login.
  // Flow: user (logged-out) clicks Pin Compare → CompareModal stashes
  // {a, b} under `pending_compare_pin` in sessionStorage, then opens
  // the LoginModal. After auth completes (email/pw OR Google) the
  // user lands back on the Dashboard with `user` truthy. This effect
  // detects the stash and re-opens CompareModal with those exact
  // slots. The CompareModal then self-checks sessionStorage on every
  // slot/user state change and auto-fires `handleTogglePin()` the
  // instant BOTH slots are locked and the pair matches — no event
  // dispatch / timing race.
  useEffect(() => {
    if (!user) return;
    let raw = null;
    try { raw = sessionStorage.getItem('pending_compare_pin'); } catch (e) { return; }
    if (!raw) return;
    try {
      const pending = JSON.parse(raw);
      if (!pending?.a || !pending?.b) {
        sessionStorage.removeItem('pending_compare_pin');
        return;
      }
      // Expire stale stash after 10 min so a forgotten attempt
      // doesn\'t silently auto-pin days later.
      if (Date.now() - (pending.ts || 0) > 10 * 60 * 1000) {
        sessionStorage.removeItem('pending_compare_pin');
        return;
      }
      // DO NOT clear the stash here — CompareModal\'s own effect
      // needs to read it after the slots lock in. CompareModal is
      // responsible for clearing it the moment it fires the pin.
      setCompareInitialTicker(pending.a);
      setCompareSnapshot({ a: pending.a, b: pending.b });
      setShowCompareModal(true);
    } catch (e) {
      try { sessionStorage.removeItem('pending_compare_pin'); } catch { /* noop */ }
    }
  }, [user]);

  // Round 95bs (Jun 2026) — Basic Calculator listener moved to a
  // global App-level component (GlobalBasicCalculator) so the Stock
  // Modal's "Calculator" link works from EVERY page, not just here.
  // The Dashboard's Tool Box tile and the Capital Tracker card now
  // dispatch the same `open-basic-calculator` event for consistency.

  // Round P108f (Feb 2026) — CSV Import guard. See state declaration
  // above for the full rationale. Called from both entry points that
  // launch the CSV importer: (a) the Capital Tracker "Import CSV"
  // button (onImportCsv prop), and (b) the Tool Box "Import CSV" tile
  // (handleToolboxAction 'import-csv' case). Behaviour:
  //   • Plus user on "All Portfolios" view (activeId==null) with 0
  //     named portfolios → open CreatePortfolioModal. The user names
  //     the portfolio, hits Continue, then CreatePortfolioModal's
  //     built-in "how" step lets them Upload CSV into the fresh pid.
  //   • Plus user on "All Portfolios" WITH existing named portfolios
  //     → toast asking them to pick a specific portfolio from the
  //     dropdown first (importing into "All Portfolios" is nonsense
  //     — it's a combined read-only view).
  //   • Free tier / any Plus user already scoped to a specific
  //     portfolio → open CsvImportModal directly (existing behaviour).
  const openCsvImportGuarded = () => {
    const snap = getPortfolioStoreSnapshot();
    if (snap?.isPlus && !snap?.activeId) {
      const pfCount = Array.isArray(snap.portfolios) ? snap.portfolios.length : 0;
      if (pfCount === 0) {
        // Zero named portfolios yet — force the naming flow. Screenshot
        // scenario from the user report.
        setShowCreatePortfolioForCsv(true);
      } else {
        // Has portfolios but is on "All". Don't silently import into
        // portfolio_id=null; make them pick a real target.
        toast.error('Pick a portfolio first', {
          description: `Import CSV can't land into "All Portfolios" — it's a combined view. Open the portfolio dropdown up top and switch to a specific portfolio, or create a new one, then click Import CSV again.`,
        });
      }
      return;
    }
    setShowCsvImportModal(true);
  };

  // Central dispatcher for Tool Box clicks. Mirrors every existing
  // tool-icon handler that lives elsewhere on the Portfolio page so
  // we have ONE source of truth here.
  const handleToolboxAction = (key) => {
    switch (key) {
      case 'all-transactions':
        // Lives inside PortfolioSummary's local state; relay via event.
        window.dispatchEvent(new Event('open-all-transactions-modal'));
        return;
      case 'basic-calculator':
        window.dispatchEvent(new Event('open-basic-calculator'));
        return;
      case 'calendar':
        navigate('/?view=calendar');
        return;
      case 'compare':
        setShowCompareModal(true);
        return;
      case 'compare-theses':
        setShowCompareTheses(true);
        return;
      case 'drip-calculator':
        setShowDripModal(true);
        return;
      case 'export-csv':
        downloadCSV('portfolio/export-csv', 'portfolio_export.csv');
        return;
      case 'google-sheet':
        window.dispatchEvent(new Event('toolbox-download-sheet-template'));
        return;
      case 'import-csv':
        openCsvImportGuarded();
        return;
      case 'my-share-page':
        window.dispatchEvent(new Event('open-share-portfolio-modal'));
        return;
      case 'rebalancer':
        navigate('/rebalancer');
        return;
      case 'tax-lots':
        navigate('/tax-lots');
        return;
      case 'account-audit':
        navigate('/tax-account-audit');
        return;
      case 'what-if':
        navigate('/whatif');
        return;
      case 'year-end':
        navigate('/year-end-report');
        return;
      default:
        return;
    }
  };

  // Map frontend sort keys to backend sort keys
  const sortKeyBackendMap = {
    'name': 'name',
    '1mo': 'perf_1m',
    '3mo': 'perf_3m',
    '6mo': 'perf_6m',
    '1yr': 'perf_1y',
    'price': 'price',
    'yield': 'yield',
    'divyr': 'divyr',
    'payout': 'payout',
    // Round P143 (Feb 2026) — Watchlist Ex-Div column. Backend field
    // is `next_ex_dividend_date` (ISO YYYY-MM-DD, sorts lexicographically).
    'exdiv': 'next_ex_dividend_date',
    // Round 97a (Feb 2026) — Mkt Cap / Sector / Industry headers in
    // All + Non-Dividend mode were silently falling back to "name"
    // sort because the map didn't translate them. Now wired through
    // to the matching backend keys so clicking the headers actually
    // sorts by the column the user clicked.
    'market_cap': 'market_cap',
    'sector':     'sector',
    'industry':   'industry',
    // Round 97c (Feb 27, 2026) — Sortable Rev Growth column for the
    // Non-Dividend table. Maps to the backend key that reads the 3yr
    // revenue CAGR from non_dividend_quality_signals.
    'rev_cagr':   'rev_cagr',
  };

  // Fetch stock list
  const fetchStocks = useCallback(async () => {
    // Round 102m4 (Feb 2026) — Don't flash the loading skeleton
    // while a search is active. The backend refetch fires when
    // sortKey/sortDir/page change, but with a search query up we
    // sort client-side against the already-loaded 311-row result
    // set — the backend page is irrelevant to what the user sees.
    // Flashing the loading state was reading as "page reloading"
    // and blowing away scroll position.
    const searchActive = !!searchQueryRef.current?.trim();
    if (!searchActive) setLoading(true);
    const backendSortKey = sortKeyBackendMap[sortKey] || 'name';
    const params = { page, per_page: 25, cached_only: true, sort_by: backendSortKey, sort_dir: sortDir, dividend_filter: dividendFilter };
    // Round P144 — hide SPACs + Warrants from the main /stocks/list
    // fetch when the "Allow SPAC & Warrant" checkbox is off. Watchlist
    // + Portfolio views use different endpoints (or fetch by explicit
    // tickers) so they aren't affected.
    if (!allowSpacsWarrants) params.hide_spacs_warrants = true;
    // Sep 2026 — "Allow: Money Market Funds" checkbox (default OFF).
    // MMFs are blank on price change / market cap / payout, so any
    // lowest-first sort surfaced 68 rows of "—". Dividend + All modes.
    if (!allowMmf) params.hide_mmf = true;

    // Retry-with-backoff wrapper for the main stock list call. If the
    // backend is cold-starting, mid-restart, or transiently times out,
    // the response sometimes comes back as an HTTP error or an empty
    // result. Both flavors used to surface as a permanent "No stocks
    // found" page until the user manually reloaded — annoying since
    // the next call usually succeeds within a second or two.
    //
    // Strategy: up to 3 attempts at 0ms / 800ms / 2400ms. Treat
    // network/HTTP errors AND "0 stocks but DB has stocks" responses
    // as retryable on the first two tries. After the final attempt
    // we render whatever we got — a real empty result will still
    // show "No stocks found" so users with genuinely-filtered queries
    // aren't kept in a permanent spinner.
    const attempts = [0, 800, 2400];
    let data = null;
    let lastErr = null;
    // Single-flight: a newer list fetch cancels any older one still in
    // the air (boot fired the same request several times).
    if (listAbortRef.current) listAbortRef.current.abort();
    const ctrl = new AbortController();
    listAbortRef.current = ctrl;
    for (let i = 0; i < attempts.length; i++) {
      if (attempts[i] > 0) {
        await new Promise((r) => setTimeout(r, attempts[i]));
      }
      if (ctrl.signal.aborted) return;
      try {
        const res = await axios.get(`${API}/stocks/list`, { params, withCredentials: true, signal: ctrl.signal });
        data = res.data;
        const isLikelyTransient = (
          (data?.total === 0 || (Array.isArray(data?.stocks) && data.stocks.length === 0))
          && i < attempts.length - 1
        );
        if (!isLikelyTransient) break;
      } catch (err) {
        if (axios.isCancel(err) || ctrl.signal.aborted) return;
        lastErr = err;
        // Network/5xx — keep retrying until we exhaust attempts.
      }
    }
    if (ctrl.signal.aborted) return;
    if (listAbortRef.current === ctrl) listAbortRef.current = null;
    try {
      if (data) {
        setStocks(data.stocks || []);
        // Round 102m3 (Feb 2026) — Do NOT overwrite filteredStocks
        // while a search is active. Sort change re-fires this fetch
        // (25-row backend page), and the old code was replacing the
        // full 311-row search result with the current page — which
        // is what the user was seeing as a "page reload". The
        // dedicated search useEffect (see below) already rebuilds
        // filteredStocks from the full stocks[] whenever the user
        // types or clears their query, so leaving it alone here is
        // safe.
        if (viewMode === 'all' && !searchQueryRef.current?.trim()) {
          setFilteredStocks(data.stocks || []);
        }
        setTotalPages(data.total_pages || 1);
        setTotalStocks(data.total || 0);
      } else if (lastErr) {
        console.error('Error fetching stocks (after retries):', lastErr);
      }
    } finally {
      if (!searchActive) setLoading(false);
    }
  }, [page, viewMode, sortKey, sortDir, dividendFilter, allowSpacsWarrants, allowMmf]);

  // Round 105c (Jun 2026) — always-fresh handle on fetchStocks for
  // long-lived closures (warm-triangles callback in fetchWatchlist).
  const fetchStocksRef = useRef(fetchStocks);
  useEffect(() => { fetchStocksRef.current = fetchStocks; }, [fetchStocks]);

  const fetchWatchlist = useCallback(async () => {
    if (!user) { setWatchlist([]); return; }
    try {
      const { data } = await axios.get(`${API}/portfolio`, { withCredentials: true });
      setWatchlist(data.map(p => p.ticker));
      // Round 72 (Feb 2026): warm the Dividend Triangle scorecard for
      // every ticker in this user's watchlist so the ▲ chip surfaces
      // on Watchlist rows without the user having to open the Triangle
      // tab first. Background fetch — we don't block the watchlist
      // render on it. On completion we kick the stocks list to re-fetch
      // so the freshly-mirrored triangle_score / triangle_legs fields
      // flow onto the visible rows.
      if (data && data.length > 0) {
        axios
          .post(`${API}/portfolio/warm-triangles`, {}, { withCredentials: true })
          .then((res) => {
            // Sep 2026 — only re-pull the table when a ticker that is
            // actually on screen was freshly computed (the endpoint now
            // reports cache hits separately), otherwise this refetch
            // fired on every landing and doubled the heaviest request.
            const fresh = new Set(res?.data?.tickers || []);
            const onScreen = (stocksRef.current || []).some((s) => fresh.has(s.ticker));
            if (res?.data?.warmed > 0 && onScreen) {
              // Round 105c (Jun 2026) — BUGFIX: call through a ref
              // instead of the closure. This callback only re-created
              // when `user` changed, so it captured a STALE fetchStocks
              // with dividendFilter='dividend' — starring a stock on
              // the All / Non-Dividend view silently reset the table
              // to dividend-only rows while the toggle stayed put.
              fetchStocksRef.current();
            }
          })
          .catch(() => { /* best-effort, never fail watchlist load */ });
      }
    } catch {}
  }, [user]);

  const fetchOwnedTickers = useCallback(async () => {
    if (!user) { setOwnedTickers([]); return; }
    try {
      // Round 92o++++++++N++++++++++ (May 2026) — Plus multi-portfolio
      // hook-in. The active portfolio id is read directly from the
      // global store at fetch time (not via React state) so we
      // re-fetch when the user switches the dropdown above the My
      // Portfolio card. The `portfolio-changed` event listener below
      // already wraps this callback, and the PortfolioSelector
      // dispatches that same event after every selection switch.
      let activePortfolioId = null;
      try {
        // Lazy import — avoids a static dep cycle with the
        // PortfolioSummary component, which in turn renders the
        // selector. Free-tier users get `activeId = null` so this
        // is a no-op for them.
        const mod = await import('@/lib/portfolioStore');
        activePortfolioId = mod.getSnapshot()?.activeId || null;
      } catch { /* free tier / store not loaded */ }
      const params = activePortfolioId ? { portfolio_id: activePortfolioId } : {};
      const data = await getOwnedTickers(params);
      setOwnedTickers(data);
      setChartsRefreshKey(k => k + 1);
    } catch {}
  }, [user]);

  // Round 95z — Section 4: persist filter choice in URL and reset to
  // page 1 on change so the table doesn't render an empty mid-pagination
  // view when the result set shrinks.
  const handleDividendFilterChange = (next) => {
    setDividendFilter(next);
    setPage(1);
    // Round 104d — every view switch starts fresh at Name A→Z.
    setSortKey('name');
    setSortDir('asc');
    try {
      const url = new URL(window.location.href);
      if (next === 'dividend') {
        url.searchParams.delete('filter');
      } else {
        url.searchParams.set('filter', next);
      }
      window.history.replaceState({}, '', url.toString());
    } catch { /* SSR / sandboxed */ }
  };

  useEffect(() => { fetchStocks(); }, [fetchStocks]);
  useEffect(() => { fetchWatchlist(); }, [fetchWatchlist]);
  useEffect(() => { fetchOwnedTickers(); }, [fetchOwnedTickers]);

  // Round 96b (Feb 2026) — Section 7 SEO. Each segmented-control filter
  // gets its own document.title so Google sees three distinct rankable
  // landing pages instead of one. Hydrated client-side in the SPA;
  // Googlebot now renders JS so this fires for crawlers too. Mirrors
  // the priorities in `SITEMAP_MAIN_ROUTES` (server.py).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const titles = {
      dividend: 'Dividend Stocks Tracker — Live Yields, Tiers & Drip Math | DiviDrip',
      all: 'All Stocks — Dividend + Non-Dividend Screener with Quality Tiers | DiviDrip',
      non_dividend: 'Non-Dividend Growth Stocks — Quality-Scored Screener | DiviDrip',
    };
    document.title = titles[dividendFilter] || titles.dividend;
  }, [dividendFilter]);

  // CSV import (and any other external mutation) only fires the global
  // `portfolio-changed` event — it doesn't have a direct ref to our
  // fetchOwnedTickers callback. Without this listener, after a CSV
  // upload (a) the Benchmark Comparison would still show 0% for "My
  // Portfolio" because chartsRefreshKey never bumps, and (b) the
  // PortfolioView at the bottom shows "no stocks" because ownedTickers
  // is still the empty array. Refetching here pulls fresh owned
  // tickers AND bumps chartsRefreshKey in one shot.
  useEffect(() => {
    const onPortfolioChanged = () => { fetchOwnedTickers(); };
    window.addEventListener('portfolio-changed', onPortfolioChanged);
    return () => window.removeEventListener('portfolio-changed', onPortfolioChanged);
  }, [fetchOwnedTickers]);

  // Round 92o++++++++N++++++++++ (May 2026) — react to watchlist
  // auto-add triggered by the Star-a-Filing flow. The backend
  // side-effects an insert into `db.portfolios` when the user stars
  // a filing for a ticker that's not yet watched. The starred-
  // filings store dispatches a `watchlist-auto-added` event with
  // the ticker; we append it to local watchlist state so the header
  // chip counter ("Watchlist 4") updates IMMEDIATELY instead of
  // lagging until the next page navigation.
  useEffect(() => {
    const onAutoAdd = (e) => {
      const t = e?.detail?.ticker;
      if (!t) return;
      setWatchlist((prev) => (prev.includes(t) ? prev : [...prev, t]));
      // Fire the generic watchlist-changed event so any other
      // consumer (useUserLimits, etc.) refreshes too.
      window.dispatchEvent(new Event('watchlist-changed'));
    };
    window.addEventListener('watchlist-auto-added', onAutoAdd);
    return () => window.removeEventListener('watchlist-auto-added', onAutoAdd);
  }, []);

  // Round 99b+ (Jul 6, 2026) — Multi-Watchlist Phase 2. When the active
  // watchlist changes (via WatchlistSelector's setActive), the store
  // fires `watchlist-changed` globally. We refetch the ACTIVE list's
  // entries here so `watchlist` state (used by every star icon, count
  // chip, snapshot card, ex-div strip, etc.) repaints atomically. Also
  // clears the local list first so stars flip off instantly, then paint
  // back on with the new list's data — matches the user's explicit
  // "clear old stars → activate → paint new stars" spec.
  //
  // Round 99c hotfix (Jul 6, 2026 evening) — user reported that after
  // starring tickers on the All Stocks view then returning to Watchlist
  // view, rows didn't appear until they opened the dropdown. Root cause:
  // `filteredStocks` derives from the paginated `stocks` cache which
  // may not include newly-starred tickers if they're outside the
  // current page window. Calling `fetchStocks()` here forces the
  // enriched cache to refresh so freshly-added tickers show up on the
  // very next render.
  useEffect(() => {
    const onWatchlistChanged = () => {
      setWatchlist([]);
      fetchWatchlist();
      fetchStocks();
    };
    window.addEventListener('watchlist-changed', onWatchlistChanged);
    return () => window.removeEventListener('watchlist-changed', onWatchlistChanged);
  }, [fetchWatchlist, fetchStocks]);

  // Round 92o++++++++N++++++++++ (May 2026) — keep `searchInput`
  // aligned with `searchQuery` when the latter changes from
  // somewhere OTHER than the typing flow (e.g. URL `?q=` hydration,
  // Clear-Search buttons, programmatic resets). The typing flow
  // updates `searchInput` itself, so this useEffect is a no-op
  // during typing (both values match) and only kicks in on external
  // mutations.
  useEffect(() => {
    setSearchInput((prev) => (prev === searchQuery ? prev : searchQuery));
  }, [searchQuery]);

  // Handle ?view=portfolio or ?view=watchlist from header navigation
  // Re-runs whenever the URL view OR the underlying ticker list changes,
  // so if watchlist/owned arrive AFTER navigation, we still populate the table.
  // The `prevViewRef` guard ensures we only reset searchQuery on an actual view
  // change — not every time `watchlist` or `stocks` array updates, which would
  // wipe what the user is typing.
  const prevViewRef = useRef('');
  const searchQueryRef = useRef('');
  useEffect(() => { searchQueryRef.current = searchQuery; }, [searchQuery]);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const view = params.get('view') || '';
    const viewChanged = prevViewRef.current !== view;
    prevViewRef.current = view;

    if (view === 'portfolio' && user) {
      setViewMode('portfolio');
      if (viewChanged) setSearchQuery('');
      setShowCalendar(false);
      // Round 91 — clear filteredStocks synchronously on view switch so
      // the previous view's rows don't briefly render in the new view's
      // table (e.g. watchlist tickers flashing under the Portfolio
      // header before the batch fetch resolves).
      if (viewChanged) setFilteredStocks([]);
      if (ownedTickers.length > 0) {
        setViewLoading(true);
        axios.get(`${API}/stocks/batch/${ownedTickers.join(',')}`, { withCredentials: true })
          .then(res => setFilteredStocks(res.data || []))
          .catch(() => setFilteredStocks(stocks.filter(s => ownedTickers.includes(s.ticker))))
          .finally(() => setViewLoading(false));
      } else {
        setFilteredStocks([]);
      }
    } else if (view === 'watchlist' && user) {
      setViewMode('watchlist');
      if (viewChanged) setSearchQuery('');
      setShowCalendar(false);
      // Round 91 — same fix for watchlist: clear immediately so portfolio
      // (or any other prior view's) tickers don't bleed through during
      // the batch fetch. Without this, mobile users on flaky networks saw
      // portfolio holdings rendered inside the Watchlist table for 5-7s
      // before the real watchlist replaced them.
      if (viewChanged) setFilteredStocks([]);
      if (watchlist.length > 0) {
        setViewLoading(true);
        axios.get(`${API}/stocks/batch/${watchlist.join(',')}`, { withCredentials: true })
          .then(res => setFilteredStocks(res.data || []))
          .catch(() => setFilteredStocks(stocks.filter(s => watchlist.includes(s.ticker))))
          .finally(() => setViewLoading(false));
      } else {
        setFilteredStocks([]);
      }
    } else if (view === 'calendar' && user) {
      setViewMode('portfolio');
      setShowCalendar(true);
    } else if (!view) {
      setViewMode('all');
      setShowCalendar(false);
      // Only reset filteredStocks back to the default page list when the view
      // actually changes, OR when the user has no active search. Otherwise a
      // re-run triggered by watchlist/stocks dep changes (e.g., clicking a
      // star) would wipe the user's live search results.
      if (stocks.length > 0 && (viewChanged || !searchQueryRef.current.trim())) {
        setFilteredStocks(stocks);
      }
    }
  }, [location.search, user, stocks, ownedTickers, watchlist]);

  // Scroll to top whenever the Calendar view is opened
  useEffect(() => {
    if (showCalendar) window.scrollTo(0, 0);
  }, [showCalendar]);

  // Deep-link: ?compare=TICKER opens the Compare modal with slot A pre-loaded.
  // Lets the StockModal's "Compare" button on any page (Screener, Challenges,
  // Sector views) navigate here and auto-open the comparison.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('compare');
    if (t) {
      setCompareInitialTicker(t.toUpperCase());
      setShowCompareModal(true);
    }
  }, [location.search]);

  // Deep-link: ?stock=TICKER[&tab=X] opens the Stock Modal with the
  // requested tab pre-selected. Used by the Tax Lot Optimizer's ticker
  // links so a click on a row jumps straight into the relevant Buy/Sell
  // (My Portfolio) tab without bouncing back to the dashboard list.
  // Strips the params from the URL once consumed so subsequent clicks
  // on the same ticker still work.
  //
  // Round 96n (Jul 3 2026) — also honors the sessionStorage
  // `dividrip:modal-state` payload so a browser refresh mid-view
  // reopens the modal on the same tab. URL param takes priority
  // over sessionStorage (explicit intent > implicit resume).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let t = params.get('stock');
    let tab = params.get('tab') || 'dividend-info';
    let accession = params.get('accession') || null;
    let fromCache = false;
    if (!t) {
      // Deep-link params were just consumed (URL rewritten below) and the
      // modal is already open — do NOT "resume" from the cache here, or the
      // transient tab the modal bounced through overrides the requested one.
      if (modalOpen) return;
      try {
        const raw = sessionStorage.getItem('dividrip:modal-state');
        if (raw) {
          const cached = JSON.parse(raw);
          const ttl = 30 * 60 * 1000;
          if (cached && cached.ticker && (Date.now() - (cached.ts || 0)) <= ttl) {
            t = cached.ticker;
            tab = cached.tab || tab;
            fromCache = true;
          } else if (cached) {
            sessionStorage.removeItem('dividrip:modal-state');
          }
        }
      } catch { /* ignore */ }
      if (!t) return;
    }
    setHighlightFiling(accession);
    handleStockClick(t.toUpperCase(), tab).catch(() => {});
    if (!fromCache) {
      // Drop the consumed ?stock and ?tab params but preserve `view`
      // so the user stays in Portfolio mode (or wherever) on close.
      const next = new URLSearchParams(location.search);
      next.delete('stock');
      next.delete('tab');
      next.delete('accession');
      const qs = next.toString();
      navigate(qs ? `${location.pathname}?${qs}` : location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Round 71m (Feb 2026): once per session, check if any of the
  // user's holdings have had a recent dividend CUT we haven't already
  // journalled about. Surface a one-click toast that opens the
  // journal entry modal pre-filled with type=dividend_cut. Skipped
  // entirely for logged-out users + cached in sessionStorage so it
  // doesn't fire on every navigation back to the dashboard.
  const [cutAlertEntry, setCutAlertEntry] = useState(null);
  useEffect(() => {
    if (!user) return;
    if (sessionStorage.getItem('div_cut_alert_shown_v1')) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/journal/dividend-cut-alerts`, { withCredentials: true });
        if (cancelled) return;
        const alerts = data?.alerts || [];
        if (!alerts.length) {
          sessionStorage.setItem('div_cut_alert_shown_v1', '1');
          return;
        }
        // One toast at a time (first un-journalled, cuts before raises).
        // "Don't show again" (top-right) mutes that specific change server-side.
        showDivChangeToast(alerts[0], { onJournal: setCutAlertEntry });
        sessionStorage.setItem('div_cut_alert_shown_v1', '1');
      } catch {
        // Silent fail — endpoint is best-effort.
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Load full ticker directory for instant client-side search
  useEffect(() => {
    axios.get(`${API}/stocks/tickers`)
      .then(res => setAllTickers(res.data || []))
      .catch(() => {});
  }, []);

  // Fetch alerts count
  useEffect(() => {
    if (!user) { setAlertsCount(0); setAlertsTriggered(0); return; }
    axios.get(`${API}/alerts`, { withCredentials: true })
      .then(res => {
        const alerts = res.data || [];
        setAlertsCount(alerts.length);
        setAlertsTriggered(alerts.filter(a => a.triggered).length);
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (stocks.length > 0 && !dataStatus) {
      const hasPrices = stocks.some(s => s.price);
      if (hasPrices) {
        setDataStatus({ ok: true, time: null, failed: 0 });
      }
    }
  }, [stocks, dataStatus]);

  // Progressive search: instant client-side, then backend enrichment.
  //
  // Round 103a (Feb 2026) — Split into two effects to kill the
  // "flash 1-2 stocks, then reload full table" flicker the user
  // reported on both typing and sorting:
  //
  //   Effect A (below) — runs ONLY when the user changes the query,
  //   view, or dividend filter. Does the instant client-side filter
  //   step and schedules the backend search.
  //
  //   Effect B (further below) — runs when `stocks` changes (page
  //   fetch, sort refetch, etc.). Only syncs `filteredStocks` when
  //   there is NO active search. This prevents the sort-refetch from
  //   dropping the 500-row backend search result back to a 1-2 row
  //   subset of the new 25-row page.
  //
  // Round 96b — Non-Dividend Expansion validity check (kept as-is).
  // Round 95p·12 — REIT/BDC pre-declaration exception: they legitimately
  // have no `frequency` yet (first dividend not announced) but are
  // structurally dividend stocks. Let them through the dividend
  // filter, and treat them as dividend rows in the `all` bucket.
  const isValidSearchRow = useCallback((s) => {
    if (!s) return false;
    const isPendingReitBdc = !!(s.is_reit || s.is_bdc) && s.pays_dividend !== true;
    // Round P140·2 (Feb 2026) — Warrants are pays_dividend=false and
    // have no frequency (they don't pay dividends at all), but a user
    // searching for their warrant holding needs the row to surface
    // even under the default `dividend` filter. Accept warrant rows
    // as long as they have a price / fetched_at signal.
    const isWarrant = !!s.is_warrant;
    // P158·9 (Feb 2026) — MMFs (SPAXX / FGXXX / VMFXX / SWVXX / …)
    // are seeded minimally: they have a $1.00 NAV price, but no
    // `frequency` field until a full refresh lands (which can be
    // never for the retail-only classes Massive doesn't recognise).
    // Accept them under EVERY filter so ticker search finds them.
    const isMmf = !!s.is_money_market_fund;
    if (dividendFilter === 'dividend') {
      if (isPendingReitBdc) return !!(s.price && s.fetched_at);
      if (isWarrant) return !!(s.name || s.price || s.fetched_at);
      if (isMmf) return !!(s.name || s.price);
      return !!(s.price && s.frequency && s.fetched_at);
    }
    if (dividendFilter === 'non_dividend') {
      // A pending REIT/BDC belongs on the dividend side even when
      // pays_dividend=false, so filter it OUT of non_dividend rows.
      if (isPendingReitBdc) return false;
      // Warrants are technically pays_dividend=false, but they're
      // NOT common-stock non-dividend equities — they belong in the
      // Warrant Screener (backlog). Suppress them here to keep the
      // non-dividend browse a clean "regular non-payer" list.
      if (isWarrant) return false;
      // MMFs DO pay income (dividend-like distributions), so they
      // don't belong in the "regular non-payer" browse either.
      if (isMmf) return false;
      return s.pays_dividend === false;
    }
    // `all` — accept dividend rows that have data plus any non-div row.
    if (isPendingReitBdc) return !!(s.price && s.fetched_at);
    if (isWarrant) return !!(s.name || s.price || s.fetched_at);
    if (isMmf) return !!(s.name || s.price);
    if (s.pays_dividend === false) return true;
    return !!(s.price && s.frequency && s.fetched_at);
  }, [dividendFilter]);

  // Ref lets Effect A reach the latest `stocks` without listing it as
  // a dep (listing `stocks` would re-run the instant filter on every
  // sort refetch — the exact flicker we're fixing).
  const stocksRef = useRef(stocks);
  useEffect(() => { stocksRef.current = stocks; }, [stocks]);

  // Effect A — user-driven search state changes.
  useEffect(() => {
    if (viewMode !== 'all') return;
    if (!searchQuery.trim()) {
      // Query cleared — resync to the current backend page. Effect B
      // will keep this in sync on any subsequent `stocks` change.
      setFilteredStocks(stocksRef.current);
      return;
    }
    const q = searchQuery.trim().toUpperCase();

    // Round 103a (Feb 2026) — Skip the "instant client-side filter"
    // step that used to run here. It filtered the current 25-row
    // backend page down to 1-2 matches for a brief moment before the
    // backend search returned the full ~500-row result, which the
    // user experienced as a jarring "table flashes 1-2 stocks then
    // reloads with the full list". `searchQuery` is already debounced
    // 1s upstream (`debouncedSetSearchQuery`), so waiting an
    // additional 100-300ms for the backend to reply — while the
    // previous filteredStocks stays visible — feels seamless.
    setSearchPage(1);

    // Backend search (short debounce) — returns ALL matches.
    const timer = setTimeout(() => {
      const searchParams = { q, limit: 500, dividend_filter: dividendFilter };
      // Round P144 — mirror the /stocks/list checkbox behaviour. When
      // "Allow SPAC & Warrant" is off, ticker/name searches also hide
      // blank-check shells + warrant tickers so the results feel
      // consistent with the main table.
      if (!allowSpacsWarrants) searchParams.hide_spacs_warrants = true;
      if (!allowMmf) searchParams.hide_mmf = true;
      axios.get(`${API}/stocks/search`, {
        params: searchParams,
        withCredentials: true,
      })
        .then(res => {
          const results = res.data || [];
          const valid = results.filter(isValidSearchRow);
          setFilteredStocks(valid);
          setSearchPage(1);
        })
        .catch(() => {});
    }, q.length === 1 ? 100 : 300);
    return () => clearTimeout(timer);
    // Intentionally omit `stocks` from the dep list — see comment
    // block at the top of this effect. `isValidSearchRow` is memoised
    // on `dividendFilter`, which is already a dep.
  }, [searchQuery, viewMode, dividendFilter, isValidSearchRow, allowSpacsWarrants, allowMmf]);

  // Effect B — resync filteredStocks to the current backend page
  // ONLY when no search is active. Runs on every `stocks` change
  // (initial fetch, sort refetch, pagination) but is a no-op when
  // the user is searching, which preserves the full 500-row search
  // result while sort/page changes happen in the background.
  useEffect(() => {
    if (viewMode !== 'all') return;
    if (searchQuery.trim()) return;
    setFilteredStocks(stocks);
  }, [stocks, viewMode, searchQuery]);

  // View mode changes
  const showAll = () => {
    setViewMode('all');
    setSearchQuery('');
    setShowCalendar(false);
    setShowNews(false);
    setFilteredStocks(stocks);
    // Clear ?view= so AppHeader buttons stop showing as active
    if (location.search) navigate('/', { replace: true });
  };

  const showPortfolio = () => {
    if (!user) { setLoginModalOpen(true); return; }
    if (ownedTickers.length === 0) { setEmptyPortfolioModal(true); return; }
    navigate('/?view=portfolio');
  };

  const showWatchlist = () => {
    if (!user) { setLoginModalOpen(true); return; }
    navigate('/?view=watchlist');
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      // Round 97a (Feb 2026) — Numeric / metric columns default to
      // DESC on first click (biggest first is the natural read).
      // String columns and Name still default to ASC. This matches
      // the user expectation across Mkt Cap ($1.04T → $11.6M),
      // Yield / Payout / Div-Yr (biggest yielders first), and the
      // 1Mo / 6Mo / 1Yr performance columns (biggest winners first).
      const DESC_FIRST = new Set([
        'price', 'market_cap', 'yield', 'divyr', 'payout',
        '1mo', '6mo', '1yr',
      ]);
      setSortDir(DESC_FIRST.has(key) ? 'desc' : 'asc');
    }
    setPage(1); // Reset to page 1 on sort change
  };

  // Default sort = name/asc. The "Clear Sort" button resets to this and
  // gets disabled when the user is already there.
  const isSortDefault = sortKey === 'name' && sortDir === 'asc';
  const isSearchActive = searchQuery.trim().length > 0 || searchStickyMode;
  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchPage(1);
    setFilteredStocks(stocks);
    setPage(1);
    // Clearing the input while in sticky mode also exits sticky mode.
    // The button in the header is the only way to re-enter it.
    setSearchStickyMode(false);
  };
  const handleClearSort = () => {
    setSortKey('name');
    setSortDir('asc');
    setPage(1);
  };

  const handleStockClick = async (ticker, forceTab = null, options = {}) => {
    // Round P112e (Jul 31, 2026) — `previewOnly` route for admin
    // queue clicks. Without this, opening a `pending_dividend_review`
    // or `pending_non_dividend_review` candidate from the admin modal
    // would call `/api/stocks/{ticker}` which HYDRATES the ticker into
    // `stock_cache` as a full row. That silently promoted candidates
    // through a side channel AND made the P112b/c auto-prune remove
    // them from the queue on next refresh. `/stocks/{ticker}/preview`
    // is explicitly designed to render the modal without persisting.
    const { previewOnly = false } = options;
    // Feb 2026 — Record this ticker in Recent Searches (skip admin
    // previewOnly clicks so the admin queue doesn't pollute the
    // user's own recent list).
    if (!previewOnly) pushRecentSearch(ticker);
    setInitialTab(forceTab || 'dividend-info');
    setInitialChartPeriod(null);
    setLoadingDetail(true);
    setModalOpen(true);
    setPrefetchedNews(null);
    axios.get(`${API}/stocks/${ticker}/news`, { withCredentials: true })
      .then(res => setPrefetchedNews(res.data)).catch(() => {});
    try {
      if (previewOnly) {
        // Preview endpoint — reads Massive/Yahoo live but never writes
        // to stock_cache. Safe for admin inspection of queue rows.
        const { data } = await axios.get(
          `${API}/stocks/${ticker}/preview`,
          { withCredentials: true, timeout: 12_000 },
        );
        setSelectedStock(data);
      } else {
        // Stage 1: race the fresh enriched fetch against a 3.5 s
        // stale-cache fallback so the modal always renders quickly. If
        // stale wins, the fresh response arrives later via onFresh and
        // upgrades the open modal in-place.
        const data = await fetchStockWithFallback(ticker, {
          timeoutMs: 3500,
          onFresh: (fresh) => setSelectedStock(fresh),
        });
        setSelectedStock(data);
      }
    } catch {
      setSelectedStock(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Round 79 — StockModal back-restore. If the user pressed Back from
  // a page opened via an in-modal link, BackOrHomeButton sent us here
  // with `location.state.reopenModalTicker`; this hook re-fires the
  // modal at that ticker exactly once on mount.
  useReopenModalOnMount(handleStockClick);

  // Round 98o (Jul 2026) — Listen for `open-stock-modal` custom events
  // dispatched by AppHeader's bell button when the user is already on
  // Dashboard (/). This opens the modal IN-PLACE without a route
  // change so the user's current view/search/filters are preserved
  // and there's no "main stock table flashes through the foggy
  // backdrop" moment. AppHeader still uses navigate() for other
  // routes so useReopenModalOnMount can re-fire after landing.
  useEffect(() => {
    const handler = (e) => {
      const { ticker, accession } = e.detail || {};
      if (!ticker) return;
      handleStockClick(ticker, accession ? 'tenk-8k-risks' : null);
    };
    window.addEventListener('open-stock-modal', handler);
    return () => window.removeEventListener('open-stock-modal', handler);
    // handleStockClick is stable via useCallback-less closure; we
    // intentionally omit it from deps to avoid re-attaching on every
    // render, since it reads from state via `set*` calls only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Aug 2026 — Refetch the stock list whenever a scheduler job that
  // rewrites stock_cache rows (e.g. `nightly_perf_refresh`) finishes.
  // The Cron History dropdown dispatches `stock-cache-refreshed` on
  // manual completion so the list updates without the admin needing
  // to reload the page and race with browser cache.
  useEffect(() => {
    const handler = () => {
      try { fetchStocksRef.current?.(); } catch { /* noop */ }
    };
    window.addEventListener('stock-cache-refreshed', handler);
    return () => window.removeEventListener('stock-cache-refreshed', handler);
  }, []);

  // Aug 2026 — "Search Stocks" header button turns on sticky search
  // mode. Same visual result as if the user typed something into
  // the box (everything above the leaderboard hides), but the box
  // itself stays empty and focused so they can start typing right
  // away. Sticky mode persists across modal opens; the only exits
  // are (a) clearing the input, (b) navigating away, or (c) refresh.
  useEffect(() => {
    const handler = () => {
      setSearchStickyMode(true);
      // Give React a tick to render the search input, then focus it.
      setTimeout(() => {
        const el = document.querySelector('[data-search-input="dashboard-primary"]');
        if (el && typeof el.focus === 'function') {
          try { el.focus(); el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch { /* noop */ }
        }
      }, 60);
    };
    window.addEventListener('dashboard-force-search-mode', handler);
    // Feb 2026 — Header's "Search Stocks" button, when clicked from
    // any non-Dashboard route, navigates to `/dashboard?search=1`
    // (the event dispatch alone wouldn't fire because Dashboard
    // isn't mounted yet). On arrival we honour that flag by
    // turning sticky mode on, focusing the input, and stripping
    // the param so the URL stays clean.
    // NOTE: an additional location-watching effect (below, deps
    // = [location.search]) covers the case where the user is
    // *already* on Dashboard in a sub-view like `?view=watchlist`
    // — that navigation doesn't remount so this mount effect
    // never re-runs. Both paths ultimately call the same handler.
    const params = new URLSearchParams(location.search);
    if (params.get('search') === '1') {
      handler();
      const next = new URLSearchParams(location.search);
      next.delete('search');
      const qs = next.toString();
      navigate(qs ? `${location.pathname}?${qs}` : location.pathname, { replace: true });
    }
    // Aug 2026 — Clicking the DiviDrip / Twylight Crow title while
    // already on Dashboard dispatches this event; treat it as an
    // explicit "reset the view" and drop out of sticky search mode
    // + clear the search box.
    const exitHandler = () => {
      setSearchStickyMode(false);
      setSearchQuery('');
      setSearchInput('');
    };
    window.addEventListener('dashboard-exit-search-mode', exitHandler);
    // Feb 2026 — Search Escape Hatch. A single Esc keypress exits
    // sticky search mode + clears the search input, so power users
    // don't have to hunt for the DiviDrip logo to reset the view.
    // Skipped when Esc is pressed inside a modal/dialog (they own
    // their own Esc handling) — we detect that by checking for an
    // open `[role="dialog"]` in the DOM.
    const escHandler = (e) => {
      if (e.key !== 'Escape') return;
      const dialogOpen = document.querySelector('[role="dialog"][data-state="open"]');
      if (dialogOpen) return;
      exitHandler();
    };
    window.addEventListener('keydown', escHandler);
    return () => {
      window.removeEventListener('dashboard-force-search-mode', handler);
      window.removeEventListener('dashboard-exit-search-mode', exitHandler);
      window.removeEventListener('keydown', escHandler);
    };
  }, []);

  // Feb 2026 — Sibling effect to the mount handler above. Fires
  // whenever `location.search` changes so that navigating from a
  // Dashboard sub-view (`/?view=watchlist` or `?view=portfolio`)
  // to `/dashboard?search=1` also flips sticky search mode on —
  // React re-uses the same Dashboard instance on those transitions
  // so the mount effect doesn't re-run. Also drops out of the
  // watchlist/portfolio view (which is why we can't just leave
  // `?view=watchlist` in the URL — the search input wouldn't be
  // rendered at all).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('search') !== '1') return;
    setSearchStickyMode(true);
    setTimeout(() => {
      const el = document.querySelector('[data-search-input="dashboard-primary"]');
      if (el && typeof el.focus === 'function') {
        try { el.focus(); el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch { /* noop */ }
      }
    }, 60);
    const next = new URLSearchParams(location.search);
    next.delete('search');
    next.delete('view');
    const qs = next.toString();
    navigate(qs ? `${location.pathname}?${qs}` : location.pathname, { replace: true });
  }, [location.search, location.pathname, navigate]);

  const handlePerfClick = async (ticker, period) => {
    setInitialTab('stock-metrics');
    setInitialChartPeriod(period || '1y');
    setLoadingDetail(true);
    setModalOpen(true);
    setPrefetchedNews(null);
    axios.get(`${API}/stocks/${ticker}/news`, { withCredentials: true })
      .then(res => setPrefetchedNews(res.data)).catch(() => {});
    try {
      const data = await fetchStockWithFallback(ticker, {
        timeoutMs: 3500,
        onFresh: (fresh) => setSelectedStock(fresh),
      });
      setSelectedStock(data);
    } catch {
      setSelectedStock(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Live-update the open modal when background Massive enrichment lands.
  // We poll /api/stocks/{ticker} every 3 s for up to 30 s whenever the current
  // modal has "holes" (missing profile/earnings/holdings/ROIC/perf). As soon as
  // a new field arrives we merge it into the open stock so the UI refreshes
  // in-place — no re-click needed. Stops immediately once all fields are
  // populated OR the modal closes.
  useEffect(() => {
    if (!modalOpen || !selectedStock?.ticker) return;
    const s = selectedStock;
    const hasHoles = (
      !s.profile_data ||
      (s.sector && !s.earnings_data) ||
      (!s.sector && !s.holdings_data) ||
      !s.return_on_invested_capital ||
      s.perf_1m == null ||
      s.perf_6m == null
    );
    if (!hasHoles) return;
    let cancelled = false;
    let tries = 0;
    const maxTries = 10; // 10 × 3s = 30 s window
    const tick = async () => {
      if (cancelled || tries >= maxTries) return;
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/stocks/${s.ticker}`, { withCredentials: true });
        if (cancelled) return;
        // Only upgrade: merge new fields that are real into the current selection.
        setSelectedStock(prev => {
          if (!prev || prev.ticker !== data.ticker) return prev;
          const merged = { ...prev };
          let changed = false;
          for (const k of Object.keys(data)) {
            const v = data[k];
            const isReal = v !== null && v !== undefined && v !== '';
            if (isReal && (merged[k] === null || merged[k] === undefined || merged[k] === '' || merged[k] === 0)) {
              merged[k] = v;
              changed = true;
            }
          }
          return changed ? merged : prev;
        });
      } catch {}
    };
    const id = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(id); };
    // Re-evaluate whenever the modal opens, or the ticker or a key field lands.
  }, [modalOpen, selectedStock?.ticker, selectedStock?.profile_data, selectedStock?.earnings_data, selectedStock?.holdings_data, selectedStock?.return_on_invested_capital, selectedStock?.perf_1m, selectedStock?.perf_6m]);

  const toggleWatchlist = async (ticker) => {
    if (!user) { setLoginModalOpen(true); return; }
    try {
      if (watchlist.includes(ticker)) {
        await axios.delete(`${API}/portfolio/${ticker}`, { withCredentials: true });
        const newWatchlist = watchlist.filter(t => t !== ticker);
        setWatchlist(newWatchlist);
        // If in watchlist view, immediately remove stock from the displayed list
        if (viewMode === 'watchlist') {
          setFilteredStocks(prev => prev.filter(s => s.ticker !== ticker));
        }
        // Round 92o++++++++N++++++++++ (May 2026) — local cascade.
        // Backend cascade-deletes `user_starred_filings` for this
        // ticker; drop the local store entries so the ★ icons in
        // the Stock Modal's 10K/8K/Risks tab flip to outline
        // immediately instead of staying gold until the next
        // hydrate.
        starredStore.forgetTicker(ticker);
      } else {
        try {
          await axios.post(`${API}/portfolio`, { ticker }, { withCredentials: true });
        } catch (err) {
          // Surface the watchlist-cap message from the backend as a toast so
          // the user knows why the star didn't stick. Backend returns a 400
          // with a `detail` string when the limit is reached.
          const detail = err?.response?.data?.detail;
          if (err?.response?.status === 400 && detail) {
            toast.error(detail);
          }
          return;
        }
        setWatchlist(prev => [...prev, ticker]);
        // Fire-and-forget refresh so the user sees fresh price/yield/dates
        // the next time they open this stock or visit the Watchlist view.
        // GET /api/stocks/{ticker} internally respects the 1h cache TTL so
        // this is cheap — it's a no-op if data is already fresh.
        axios.get(`${API}/stocks/${ticker}`, { withCredentials: true })
          .catch(() => { /* ignore — refresh is best-effort */ });
      }
      window.dispatchEvent(new Event('watchlist-changed'));
    } catch {}
  };

  const clearWatchlist = async () => {
    if (!user || clearingWatchlist) return;
    setClearingWatchlist(true);
    try {
      const { data } = await axios.post(`${API}/watchlist/clear`, {}, { withCredentials: true });
      const removed = data?.removed ?? 0;
      setWatchlist([]);
      setFilteredStocks([]);
      setShowClearWatchlistDialog(false);
      window.dispatchEvent(new Event('watchlist-changed'));
      // Round 92o++++++++N++++++++++ (May 2026) — backend
      // cascade-clears every starred filing for this user when the
      // watchlist is wiped. Mirror that in the local store so any
      // open Stock Modal's ★ icons flip back to outline.
      starredStore.forgetAll();
      toast.success(`Watchlist cleared`, {
        description: removed === 1 ? '1 stock removed.' : `${removed} stocks removed.`,
      });
    } catch (e) {
      toast.error("Couldn't clear your watchlist", {
        description: 'Please try again in a moment.',
      });
      /* leave dialog open on error so the user can retry */
    } finally {
      setClearingWatchlist(false);
    }
  };

  const resetPortfolio = async () => {
    if (!user || resettingPortfolio) return;
    setResettingPortfolio(true);
    try {
      // Round P118 (Feb 2026) — CRITICAL data-loss fix.
      //
      // OLD BUG: this button ALWAYS sent `{confirm_global: true}` with
      // NO portfolio_id, which hit the backend's legacy global-wipe
      // path (server.py:36078 `delete_many({user_id})`). When a Plus
      // user was viewing a single portfolio (e.g. Test 1) and clicked
      // this button, they reasonably expected it to nuke only THAT
      // portfolio — but the wipe took out every sibling portfolio
      // too. The button IS hidden on the "All Portfolios" summary
      // view (isAllReadOnly), so it should NEVER have been sending
      // confirm_global — it always fires from within one specific
      // portfolio's context.
      //
      // FIX: read the currently-active portfolio_id from the store
      // and scope the wipe. If somehow there's no active id (legacy
      // free-tier user with zero named groups), fall back to the
      // legacy path — those users have exactly one portfolio and no
      // siblings to accidentally destroy.
      const snap = getPortfolioStoreSnapshot();
      const scopedPid = dashActivePortfolioId || snap?.activeId || null;
      const body = scopedPid
        ? { portfolio_id: scopedPid }
        : { confirm_global: true };
      const { data } = await axios.post(`${API}/portfolio/reset-all`, body, { withCredentials: true });
      // Refresh all portfolio-related state everywhere
      await fetchOwnedTickers();
      window.dispatchEvent(new Event('portfolio-changed'));
      setShowResetPortfolioDialog(false);
      // Round P108e — surface Undo toast so an accidental reset is
      // recoverable for 15 minutes. Global reset has the broadest
      // blast radius (positions + cash + drip + share slug + income
      // goal), all captured in the snapshot.
      if (data?.undo_snapshot_id) {
        showUndoToast(data, {
          successMessage: scopedPid
            ? `"${data.undo_portfolio_name || 'Portfolio'}" reset — sibling portfolios untouched`
            : 'Portfolio reset — everything cleared',
          onUndone: () => { fetchOwnedTickers(); },
        });
      } else {
        toast.success('Portfolio reset', {
          description: scopedPid
            ? 'Wiped this portfolio only: positions, cash, transactions, tax-lots, DRIP overrides. Sibling portfolios untouched.'
            : 'Wiped: positions, cash, transactions, tax-lot history, DRIP overrides, income goal, share page, and dividend reminders. Your watchlist is untouched.',
        });
      }
    } catch (e) {
      toast.error("Couldn't reset your portfolio", {
        description: 'Please try again in a moment.',
      });
      /* leave dialog open on error so the user can retry */
    } finally {
      setResettingPortfolio(false);
    }
  };

  return (
    <div className="min-h-screen min-h-[100svh] bg-background transition-colors duration-500 relative" data-testid="dashboard">
      {/* Round 96z (Jul 2026) — Split alert banner sits at the very
          top of the dashboard, above all other content. Self-hides
          when the user has no visible split alerts. */}
      <SplitAlertBanner />
      {/* Streak rows — hoisted to the very top of the page on the
          main landing (viewMode==='all' with no active search). Per
          user request (May 2026) the Search / Screener / Challenge /
          Badge Key toolbar row must sit BELOW both streak boxes on
          every viewport size so users see the "what just happened"
          recap before any navigation controls. Mobile order ends up
          as: Streaks Extended → Streaks At Risk → (Challenge+Badge
          Key) → Search → Screener line → table. Desktop order:
          Streaks Extended → Streaks At Risk → single toolbar row →
          info text → table. */}
      {viewMode === 'all' && !isSearchActive && !showCalendar && (
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4" data-testid="streak-rows-hoisted">
          <LateCycleBanner />
          {/* Sep 2026 — every full-width card is separated by the same
              16px gap (space-y-4); children carry no outer margins. */}
          <div className="space-y-4" data-testid="dashboard-card-stack">
          <DashboardSection title="Fed & Treasury Watch" icon={Landmark} iconClassName="text-emerald-700 dark:text-emerald-400" testId="macro-pulse-section" borderClassName="border-slate-400/80 dark:border-border">
            <p className="text-[11px] font-bold text-blue-600 dark:text-blue-400 mb-2" data-testid="macro-tap-hint">
              Click or tap a chip for the full breakdown.
            </p>
            <MacroContextStrip onOpenStock={handleStockClick} />
          </DashboardSection>
          {/* Round P126 (Feb 2026) — Coverage Radar cards MOVED to
              the Watchlist view (rendered above WatchlistSummaryCard
              in stacked full-width layout) per user request. They
              were card-clutter on the leaderboard, and only relevant
              to watchlisted tickers anyway. */}
          <DashboardSection title="Kings - Aristocrats - Achievers" icon={Crown} iconClassName="text-amber-600 dark:text-yellow-400" testId="tiers-section" bodyClassName="space-y-3 [&>*]:mb-0" borderClassName="border-amber-600/45 dark:border-yellow-400/45">
            <p className="text-[11px] font-bold text-blue-600 dark:text-blue-400" data-testid="tiers-tap-hint">
              Click or tap a chip for the full breakdown.
            </p>
            <StreakHighlightRow onStockClick={handleStockClick} ownedTickers={ownedTickers} />
            <StreakAtRiskRow onStockClick={handleStockClick} ownedTickers={ownedTickers} />
            {/* Round 95f + P108av+6 (Feb 2026) relocation — "Cut from List"
                sits directly under Streak-At-Risk so the "at-risk" →
                "cut" narrative reads top-to-bottom. Members removed from
                the curated tier lists this quarter. Null-renders when
                no cuts exist (stays invisible until one happens). */}
            <TierCutsRow onStockClick={handleStockClick} ownedTickers={ownedTickers} />
            {/* P107 — Top-3 undervalued per tier, links to heatmap pages. */}
            <HeatmapTop3Strip />
          </DashboardSection>
          {/* Round 96n → Feb 2026 relocation. Squeeze Radar moved out
              from directly under MacroContextStrip to sit under the
              Heatmap "Most Undervalued" strip so the two "current
              opportunity" surfaces cluster together above Top Movers.
              Hidden automatically when the nightly warmer hasn't
              produced a snapshot or when the snapshot is stale >48h. */}
          <SqueezeRadarStrip onOpenStock={handleStockClick} />
          {/* Sep 2026 — 7am–10am Breakaway: small-cap gap-up + 3× minute
              volume + today's news + ≤20M float, scanned every 5 min. */}
          <BreakawayStrip onOpenStock={handleStockClick} />
          {/* Round 86 (Feb 2026) — Top Dividend Movers relocated here per
              user request so the day's biggest dividend gainers/losers
              sit alongside the streak highlights rather than inside the
              stock table area. */}
          <TopMoversWidget
            onStockClick={handleStockClick}
            watchlist={watchlist}
            onToggleWatchlist={toggleWatchlist}
          />
          {/* Feb 2026 — Movers News strip. Hidden via NEWS_STRIP_ENABLED
              backend env flag (admins always see it). Sep 2026 — moved
              between Top Dividend Movers and Stage 2 Leaders. */}
          <MoversNewsStrip />
          {/* Feb 2026 — Capital Quality Top 10. Surfaces the highest-
              ranked non-dividend tickers from the weekly EQR cron so
              the non-div audience sees alpha picks without having to
              open every stock modal individually. Companion to
              TopMoversWidget; quietly null-renders when the cron
              hasn't run yet.
              Round 96f (Feb 2026) — Currently admin-only. When the
              Stripe paywall flips on, the gate becomes
              `user?.role === 'admin' || user?.tier === 'pro'` so paying
              non-div tier subscribers also see it. Free dividend-only
              users never see it (the leaderboard is a non-dividend
              product surface). */}
          {user?.role === 'admin' && (
            <Stage2Leaderboard
              onStockClick={handleStockClick}
              watchlist={watchlist}
              onToggleWatchlist={toggleWatchlist}
            />
          )}
          {user?.role === 'admin' && (
            <CapitalAnalyticsLeaderboard
              onStockClick={handleStockClick}
              watchlist={watchlist}
              onToggleWatchlist={toggleWatchlist}
            />
          )}
          </div>
          <WhatChangedBanner />
          {/* Round 86 (Feb 2026) — Admin buttons are now wrapped in a
              collapsible container that:
                • Renders NOTHING for non-admin users (defense in depth
                  on top of each button's own admin guard).
                • Shows a single "Show admin buttons" toggle for the
                  admin, hidden by default to keep the dashboard clean.
                • Open/closed preference persists via localStorage. */}
          <AdminButtonsCollapse>
            {/* Round 92o++++++++N++++++++ — Cron history dropdown
                sits ABOVE the per-cadence groups so the admin can
                glance at last-run results without expanding any
                group. Polls every 60s while open. */}
            <AdminCronHistoryDropdown />
            {/* Round 92 (Feb 2026) — Admin buttons consolidated into 6
                ordered groups, top → bottom by cadence + purpose:
                  1. Daily Refresh  (24h cron + manual)
                  2. Weekly Warmers (7-day cron + manual)
                  3. Dividend Hygiene (on-demand)
                  4. Streaks (on-demand)
                  5. Community moderation (as-needed)
                  6. How-To Feedback (as-needed)
                Each group is collapsible; child buttons retain their
                own status/cooldown/modal logic. */}
            <AdminButtonGroup
              icon="🌅" label="Daily Refresh" cadence="24h"
              tone="amber" testid="admin-group-daily"
              storageKey="dividrip:admin-group-daily"
            >
              {/* Daily warmer/snapshot buttons have moved to the
                  2 AM cron orchestrator above. These manual buttons
                  remain — they're not part of the chain. */}
              <AdminWarmerButton
                prefix="ratios-refresh"
                label="Refresh Ratios"
                testid="admin-ratios-refresh"
                tone="amber"
                description="Bulk-pulls Massive /v1/ratios for every cached ticker (P/E, P/B, ROA, ROE, debt-to-equity, FCF). 24h cooldown."
              />
              <AdminWarmerButton
                prefix="us-market-universe-refresh"
                label="US Market Universe"
                testid="admin-universe-refresh"
                tone="amber"
                description="Paginates Massive /v3/reference/tickers for type ∈ {CS, ADRC, ETF} and upserts every active US-listed Common Stock, ADR, and ETF into the us_market_universe collection. Existing classification flags are preserved across refreshes. ~10,000 tickers; runs in ~10–20s. 12h cooldown."
              />
              {/* Round 95m (Jun 2026) — User-requested ADR badging
                  cron. Walks `us_market_universe` rows with
                  classification='adr' and stamps `is_adr=True` on the
                  matching `stock_cache` rows. Uses Massive's own
                  ADRC classification as the source of truth, catching
                  edge-case names like KSPI ("American Depository
                  Shares" with O instead of A) that the regex-based
                  detect_is_adr misses. Runs as the LAST step of the
                  daily cron chain (after universe refresh) so newly
                  IPO'd ADRs are badged on the same day. 24h cooldown. */}
              <AdminWarmerButton
                prefix="us-market-universe-adr-backfill"
                label="ADR Badge Backfill"
                testid="admin-universe-adr-backfill"
                tone="amber"
                description="Walks every us_market_universe row with classification='adr' (Massive's authoritative ADRC list) and stamps is_adr=true on the matching stock_cache row. Catches ADRs whose company name doesn't contain 'ADR' / 'ADS' / 'American Depositary' so detect_is_adr's regex would otherwise miss them (e.g. KSPI / Kaspi.kz). Runs as the LAST step of the daily cron chain. ~1,500 ADRs in the universe; matches only currently-cached tickers (no stock_cache bloat). 24h cooldown."
              />
              {/* Round 95s — at-a-glance Yahoo-touch indicator. Now
                  that every cron is Massive-primary, healthy state
                  is zero touches. Surfaces tickers that fell back to
                  Yahoo so admin can spot persistent Massive gaps. */}
              <AdminYahooTouchPill />
              {/* Round 102f (Feb 2026) — On-demand Squeeze Radar
                  warmer. The cron runs nightly, but if preview
                  slips or an admin wants to reflect a fresh Massive
                  short-interest snapshot (bi-weekly cadence),
                  manual firing keeps the dashboard strip current. */}
              <AdminSqueezeRadarButton />
            </AdminButtonGroup>

            <AdminButtonGroup
              icon="📅" label="Weekly Warmers" cadence="7-day"
              tone="violet" testid="admin-group-weekly"
              storageKey="dividrip:admin-group-weekly"
            >
              <AdminQualityRebackfillButton />
              <AdminTreasuryClassifyButton />
              <AdminWarmerButton
                prefix="quarterly-financials"
                label="Quarterly Financials"
                testid="admin-quarterly-fin"
                tone="violet"
                description="Bulk re-derives ROIC, FCF margin, net margin, gross margin, EPS growth, and revenue stability from Massive's quarterly statements. 90-day cooldown."
              />
              <AdminWarmerButton
                prefix="us-market-universe-classify-dividends"
                label="Classify Non-Dividend"
                testid="admin-universe-classify"
                tone="violet"
                description="Walks every row in us_market_universe and queries Massive /v3/reference/dividends for the most recent ex-date. Anything with no dividend in the last 24 months gets flagged is_non_dividend=true. Required before any non-dividend screener can show clean results. ~10,000 tickers @ 0.08s each ≈ 14 min. 7-day cooldown."
              />
              {/* Round 96q (Jul 4 2026) — One-time (6h cooldown) CUSIP →
                  ticker backfill via OpenFIGI free API. Lights up 13-F
                  holdings for tickers whose CUSIP couldn't be matched
                  via the fuzzy issuer-name path used by the weekly
                  13-F bucketing cron (~40% of the universe). Expected
                  runtime ~2 hours for 15,000 unmatched CUSIPs at
                  OpenFIGI's 25 req/min free-tier ceiling. After it
                  finishes, kick the weekly 13-F bucketing cron so
                  the newly-mapped CUSIPs land in user-facing tables. */}
              <AdminWarmerButton
                prefix="cusip-backfill"
                label="CUSIP → Ticker Backfill"
                testid="admin-cusip-backfill"
                tone="violet"
                description="One-time backfill: scans Massive's 13-F firehose for CUSIPs missing from cusip_ticker_map (weighted by market value so the biggest holdings resolve first), then batches them to OpenFIGI's free /v3/mapping endpoint (100 IDs per call, 25 calls/min, no API key needed). Lifts 13-F match rate from ~73% toward ~95%. Safe to re-run — idempotent. 6h cooldown."
              />
              {/* Round 98h (Jul 2026) — Form 3 (Initial Insider
                  Positions) warmer manual-fire button. Cron already
                  runs nightly at 3:30 AM ET, but exposing the button
                  lets an admin catch up top_pct / founder_stake
                  coverage on demand — critical after shares_outstanding
                  backfills or when new tickers are added to the
                  universe (~30s runtime for ~9,300 tickers, 22h
                  cooldown). Populates form3_founder_stake summary
                  + insider_activity_cache in one shot. */}
              <AdminWarmerButton
                prefix="form3-warmer"
                label="Form 3 Insider Warmer"
                testid="admin-form3-warmer"
                tone="violet"
                description="Refreshes Form 3 (Initial Insider Positions) baseline holdings across the entire common-stock universe. Writes: (1) per-ticker insider_activity_cache row for the Stock Modal Insider tab, and (2) compact form3_founder_stake summary on stock_cache powering the Founder Stake Detector badge + Founder-Led Screener filters. ~30s for ~9,300 tickers. Cron runs nightly 3:30 AM ET; fire manually after shares_outstanding refreshes or when new tickers land in the universe. 22h cooldown."
              />
              {/* Round 98j (Jul 2026) — IPO warmer manual-fire button.
                  Cron runs nightly 3:45 AM ET. Fire manually after
                  major IPO events to catch new listings same-day. */}
              <AdminWarmerButton
                prefix="ipo-warmer"
                label="IPO Watch Warmer"
                testid="admin-ipo-warmer"
                tone="violet"
                description="Pulls Massive /vX/reference/ipos across pending/new/history buckets for the last 180 days and upserts into ipo_events. Powers the /ipo-watch SEO landing page + SPAC Volume Signal. Runs in < 1 second via 3-parallel async fetch. Cron runs nightly 3:45 AM ET; fire manually when a major IPO event (like SPCX) lands mid-day. 22h cooldown."
              />
              {/* Round 98k (Jul 2026) — Rest of the previously-invisible
                  warmer suite. All have API endpoints via
                  _make_warmer_endpoints but were missing manual-fire
                  buttons in the admin UI. Grouped here so admins can
                  see the full cron surface at a glance. */}
              <AdminWarmerButton
                prefix="treasury-yields"
                label="Treasury Yields Refresh"
                testid="admin-treasury-yields"
                tone="violet"
                description="Refreshes the US Treasury yield curve (1M / 3M / 6M / 1Y / 2Y / 5Y / 10Y / 30Y) from FRED via Massive. Powers the Yield-vs-Treasury comparisons on the Stock Modal, the Real Yield toggle on the Screener, and the risk-free rate baseline for DCF calculations. Cron runs nightly; fire manually if Fed announces a policy move mid-day. 22h cooldown."
              />
              {/* Round 100 (Jul 2026) — One-shot 15-year dividend cache
                  expansion. Fixes the ZTS-class bug where non-curated
                  tickers show "0 yr" on the Triangle streak check
                  because the growth cache only holds 5 years of
                  annual dividend data. Long-running (~40 min) — safe
                  to run overnight before regular crons. Idempotent. */}
              <AdminWarmerButton
                prefix="dividend-15yr"
                label="Dividend Cache 15yr"
                testid="admin-dividend-15yr"
                tone="emerald"
                description="ONE-SHOT: Expands dividend_growth_cache.annual_data from 5 years to 15 years for every ticker in the universe, using Massive /v3/reference/dividends. After each successful expansion, also backfills dividend_streak_years on stock_cache with the observed floor — so tickers like ZTS (12-year real streak, ~4 visible in 5-year cache) get accurate streak values across the Triangle checklist, Streak Risk chart, and Div Growth Tab. Idempotent — safe to re-fire. ETA ~40 min for the full universe (~700 rows). Run overnight BEFORE regular crons kick in."
              />
              {/* Round 101 (Jul 2026) — Damodaran ERP dataset refresh.
                  Fire once a year in mid-January after his annual
                  update lands. Cached geometric-mean real S&P
                  return powers the Valuation Matrix historical
                  benchmark chip. */}
              <AdminWarmerButton
                prefix="damodaran-benchmark"
                label="Damodaran Benchmark"
                testid="admin-damodaran-benchmark"
                tone="fuchsia"
                description="Downloads Aswath Damodaran's histretSP.xls (NYU Stern) and caches the geometric-mean US equity REAL return from his 'Nominal vs Real Data' sheet. Powers the historical benchmark chip on the Valuation Matrix. Fire once per year mid-January after his annual update lands (his file is updated the first week of every year). Fast — takes seconds. Idempotent."
              />
              <AdminWarmerButton
                prefix="inflation"
                label="Inflation (CPI) Refresh"
                testid="admin-inflation"
                tone="violet"
                description="Refreshes CPI-U (headline inflation) + Core CPI + TIPS breakeven yield curve. Powers the Real Yield toggle on the Screener, real-return calculations in the Portfolio, and inflation-adjusted DCF/dividend growth projections. Cron runs monthly around CPI release day; fire manually right after a BLS CPI report lands. 22h cooldown."
              />
              <AdminWarmerButton
                prefix="cef-nav-history"
                label="CEF NAV History"
                testid="admin-cef-nav-history"
                tone="violet"
                description="Backfills historical Net Asset Value (NAV) time-series for all Closed-End Funds in the universe, enabling premium/discount tracking + CEF IPO discount-widening alpha analysis on the /ipo-watch page. ~5-15 minutes depending on how many CEFs need history. Cron runs weekly; fire manually when new CEFs are added. 22h cooldown."
              />
              <AdminWarmerButton
                prefix="thirteen-f-bucket"
                label="13F Institutional Bucketing"
                testid="admin-13f-bucket"
                tone="violet"
                description="Weekly aggregation of SEC Form 13F institutional holdings — buckets each ticker by top-holder type (index fund / hedge fund / pension / insurance) so the Institutional Ownership tab on the Stock Modal can show composition. Depends on cusip-backfill running FIRST so unresolved CUSIPs get mapped to tickers. ~10-20 minutes for the full universe. Weekly cron; fire manually right after CUSIP backfill completes. 22h cooldown."
              />
              <AdminWarmerButton
                prefix="equity-quality-rank"
                label="Capital Analytics Rank Refresh"
                testid="admin-equity-quality-rank"
                tone="violet"
                description="Recomputes the 4-factor Capital Analytics composite (buyback yield / CFROI / momentum / Altman Z) and the resulting rank + BUY/WATCH/AVOID verdict for every non-dividend ticker. Powers the Capital Analytics badge on the Stock Modal + non-div sort key on the Screener. ~2-5 minutes. Fire manually after new financial statements are backfilled. 22h cooldown."
              />
              <AdminUniverseHealthPill />
            </AdminButtonGroup>

            <AdminButtonGroup
              icon="🛡️" label="Dividend Hygiene" cadence="on-demand"
              tone="emerald" testid="admin-group-dividend-hygiene"
              storageKey="dividrip:admin-group-dividend-hygiene"
            >
              <AdminDividendDatesButton />
              <AdminPendingDividendReviewButton onStockClick={handleStockClick} />
              <AdminCheckRecycledTickerButton onStockClick={handleStockClick} />
              <AdminPreferredTermsButton onStockClick={handleStockClick} />
              <AdminExpiredWarrantsButton onStockClick={handleStockClick} />
              <AdminRecycledLockedPanel onStockClick={handleStockClick} />
              <AdminPerfRefreshSkippedPanel onStockClick={handleStockClick} />
              <AdminSlowRequestsButton />
              <AdminSpacCleanupButton />
              <AdminDividendGateAuditButton onStockClick={handleStockClick} />
              {/* Round 95p — one-time SIC sector backfill; hides
                  itself forever once the run completes. */}
              <AdminSectorBackfillButton />
              <AdminRecoverMissingTickersButton />
              <AdminTagBabyBondsButton />
              <AdminDataProvenanceHealthButton onStockClick={handleStockClick} />
              <AdminHydrateNonDividendUniverseButton />
              <AdminPendingNonDividendReviewButton />
              <AdminDivToNonDivButton onStockClick={handleStockClick} />
              <AdminNonDivToDivButton onStockClick={handleStockClick} />
              <AdminRefreshFamousHoldersButton />
              <AdminScrubStructuralNonDivButton />
              <AdminDeleteDefaultAdminButton />
              <AdminScoreNonDivQualityButton />
              <AdminNonDivPerfWarmerButton />
              <AdminWarmFinancialHealthButton />
              <AdminWarmYieldHistoryButton />
              <AdminDataHygieneButton />
            </AdminButtonGroup>

            <AdminButtonGroup
              icon="🏆" label="Streaks" cadence="on-demand"
              tone="sky" testid="admin-group-streaks"
              storageKey="dividrip:admin-group-streaks"
              statusBadge={<TierResyncStatusBadge />}
            >
              <AdminRebuildStreaksButton />
              <AdminResyncTiersButton />
              <AdminReconcileTierCutsButton />
              <AdminAutoReconcileForeignAdrsButton />
            </AdminButtonGroup>

            <AdminMassiveApiWatchCard />
            <AdminCommunityButton />
            <AdminMmfSeedButton />
            <AdminHowToFeedbackButton onStockClick={handleStockClick} />
            <AdminLearnGuideFeedbackButton onStockClick={handleStockClick} />
            <AdminBlocklistTickerButton />
            <AdminMarkLiquidatedButton />
            <AdminHydrateBenchmarkEtfsButton />
          </AdminButtonsCollapse>
        </div>
      )}

      {/* Community invitation card — Phase 1 Step 5 (Feb 2026).
          Self-hides for non-logged-in users, existing members, and
          users who dismissed within the last 14 days. */}
      {COMMUNITY_ENABLED && viewMode === 'all' && !isSearchActive && !showCalendar && (
        <JoinCommunityCard />
      )}

      {/* Round 80b — Community chatter on tickers the viewer holds.
          Self-hides if user has no positions or no posts exist. */}
      {COMMUNITY_ENABLED && viewMode === 'all' && !isSearchActive && !showCalendar && user && (
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-1">
          <MyPortfolioCommunityPosts />
        </div>
      )}

      {/* Search + Refresh */}
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Round P111o (Feb 2026) — BackBtn declared BEFORE the
            nav-snippets IIFE so PortfolioSummary (rendered below)
            can also receive it via the `backSlot` prop for the
            inline-with-selector desktop layout. */}
        {(() => {
          const BackBtn = (
            <BackOrHomeButton
              testid="dashboard-back-or-home-btn"
              showEscHint={false}
              className="shrink-0"
            />
          );
          const makeSearchBox = (testid) => (
            <div className="relative flex-1 min-w-0 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid={testid}
                data-search-input="dashboard-primary"
                type="text"
                placeholder="Search ticker or name..."
                value={searchInput}
                onChange={(e) => {
                  const v = e.target.value;
                  // Update the field's displayed value instantly so
                  // the user never sees lag while typing.
                  setSearchInput(v);
                  // Typing while on Watchlist/Portfolio/Calendar jumps the user
                  // back to the full All-Stocks view so search runs against the
                  // whole database (4,500+ tickers). Preserves the typed chars
                  // — never calls setSearchQuery('') which would erase them.
                  if (viewMode !== 'all' || showCalendar) {
                    setViewMode('all');
                    setShowCalendar(false);
                    setShowNews(false);
                    if (location.search) navigate('/', { replace: true });
                  }
                  // Debounce the actual search trigger by 1.5s so
                  // fast typing doesn't fire 8 setSearchQuery calls
                  // in a row and scramble the result list.
                  debouncedSetSearchQuery(v);
                }}
                className="pl-9 h-9 text-sm border-2 border-amber-700 dark:border-amber-300/70 focus-visible:ring-amber-500/40 focus-visible:border-amber-500"
              />
            </div>
          );
          const SearchBox = makeSearchBox('search-input');
          const ScreenerLink = (label) => (
            <Link to="/screener" data-testid="screener-link">
              <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors shrink-0 px-2.5 py-2">
                <Filter className="w-4 h-4 text-primary" />
                <span>{label}</span>
              </button>
            </Link>
          );
          const ChallengeDD = (showLabel = true) => (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors shrink-0 px-2.5 py-2" data-testid="challenge-dropdown-trigger">
                  <span className="text-base">{'\u{1F451}'}</span>
                  {showLabel && <span>Challenge</span>}
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" data-testid="challenge-dropdown-menu">
                <Link to="/kings-challenge">
                  <DropdownMenuItem className="cursor-pointer gap-2" data-testid="challenge-kings-link">
                    <span>{'\u{1F451}'}</span> Kings Challenge
                  </DropdownMenuItem>
                </Link>
                <Link to="/aristocrats-challenge">
                  <DropdownMenuItem className="cursor-pointer gap-2" data-testid="challenge-aristocrats-link">
                    <span>{'\u{1F3C6}'}</span> Aristocrats Challenge
                  </DropdownMenuItem>
                </Link>
                <Link to="/achievers-challenge">
                  <DropdownMenuItem className="cursor-pointer gap-2" data-testid="challenge-achievers-link">
                    <span>{'\u{1F396}\uFE0F'}</span> Achievers Challenge
                  </DropdownMenuItem>
                </Link>
              </DropdownMenuContent>
            </DropdownMenu>
          );
          const DesktopTierLegend = (
            <div className="hidden sm:flex items-center justify-end gap-2.5 text-xs text-muted-foreground shrink-0 ml-auto" data-testid="tier-legend">
              <BadgeKey />
            </div>
          );

          return (
            <>
              {/* DESKTOP toolbar — unchanged single row with all controls.
                  Round 96c (Jul 2026) — search box hidden on Watchlist
                  and Portfolio views per user request. Users search
                  from the All Stocks view; keeping the search box on
                  Watchlist/Portfolio wasted horizontal space that now
                  goes to the Badge Key + Screener + Challenge cluster.
                  Round 99b+ (Jul 6, 2026) — WatchlistSelector on the
                  desktop toolbar too, sits right after BackBtn on the
                  Watchlist view only. Free users see a compact pencil-
                  rename affordance; Plus users get the full dropdown. */}
              <div className="hidden sm:flex items-center gap-2 flex-wrap">
                {viewMode !== 'all' && viewMode !== 'portfolio' && BackBtn}
                {viewMode === 'watchlist' && !showCalendar && <WatchlistSelector />}
                {viewMode !== 'watchlist' && viewMode !== 'portfolio' && SearchBox}
                {/* Round P111o (Feb 2026) — Back button on Portfolio
                    view is now rendered by PortfolioSummary via the
                    `backSlot` prop, positioned INLINE with the
                    PortfolioSelector chip (to its left) instead of
                    up here on its own row. Mobile toolbar below
                    still owns the <sm Back copy. */}
                {/* Round P111m (Feb 2026) — Stock Screener + Challenge
                    were HERE on desktop; moved into the AppHeader
                    link row (How To / Glossary / Learn / Screener /
                    Challenge). Mobile toolbar below still owns the
                    <sm copies, so phone view is untouched. */}
                {/* Round P111n (Feb 2026) — Badge Key hidden on
                    Portfolio view on desktop. The stock rows at
                    the bottom of Portfolio already carry their own
                    Badge Key affordance, so the header copy was
                    duplicative. Mobile toolbar keeps the mobile
                    Badge Key row (line 2056) unchanged. */}
                {viewMode !== 'portfolio' && DesktopTierLegend}
              </div>

              {/* MOBILE toolbars — different layout per view */}
              <div className="sm:hidden">
                {/* Portfolio (mobile): Line 1 = All Stocks + Screener + Challenge,
                    Line 2 = Tool Box launcher + Badge Key (Round 86c).
                    Round 96c (Jul 2026) — Line 3 search input REMOVED
                    per user request. Search is now only available on
                    the All Stocks view. */}
                {viewMode === 'portfolio' && !showCalendar && (
                  <>
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      {BackBtn}
                      {ScreenerLink('Screener')}
                      {ChallengeDD(true)}
                    </div>
                    {/* Round 86c (May 2026) — Toolbox + Badge Key row.
                        Moved here from the right-side of the search bar
                        because on narrow phone widths they were getting
                        cropped off-screen. Sits as its own row directly
                        between the back/screener/challenge top row and
                        the (removed) search input. */}
                    <div className="flex items-center justify-center gap-2 mt-2">
                      <button
                        type="button"
                        onClick={() => window.dispatchEvent(new Event('open-toolbox-modal'))}
                        data-testid="toolbox-launcher-mobile-portfolio"
                        className="shrink-0 inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md border border-fuchsia-300/60 dark:border-amber-200/40 bg-gradient-to-r from-fuchsia-50 to-amber-50 dark:from-fuchsia-950/30 dark:to-amber-950/30 active:scale-95 transition-all"
                        title="Open the Tool Box — every portfolio tool in one place"
                      >
                        <img src="/icons/toolbox.png" alt="" className="w-5 h-5 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        <span className="text-xs font-bold text-fuchsia-800 dark:text-amber-100">Tool Box</span>
                      </button>
                    </div>
                  </>
                )}

                {/* Calendar: Line 1 = All Stocks + Search, Line 2 = Screener + Challenge */}
                {showCalendar && (
                  <>
                    <div className="flex items-center gap-2">
                      {BackBtn}
                      {makeSearchBox('search-input-mobile-calendar')}
                    </div>
                    <div className="flex items-center justify-center gap-3 mt-2">
                      {ScreenerLink('Screener')}
                      {ChallengeDD(true)}
                    </div>
                  </>
                )}

                {/* Round 97l (Feb 2026) — Watchlist (mobile) top toolbar.
                    Back arrow + Screener (icon+label) + Challenge
                    dropdown. Badge Key was moved OUT of this row to
                    its own dedicated line further down (between the
                    News/TimeMachine/Alerts row and the 3-way switch +
                    LIVE pill row) per UX request. The old "Clear
                    Search / Clear Sort" trio that used to live above
                    the search box was also removed — those two
                    buttons already exist in the `mobile-search-row`
                    right next to the search input they affect. */}
                {!showCalendar && viewMode === 'watchlist' && (
                  <>
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      {BackBtn}
                      {ScreenerLink('Screener')}
                      {ChallengeDD(true)}
                    </div>
                    {/* Round 99b+ (Jul 6, 2026) — Multi-Watchlist Phase 2
                        selector. Free users see a compact "Main" label
                        with a pencil to name it. Plus users see the full
                        dropdown with create / rename / delete + 7-day
                        grace-period chip on lapsed-Plus lists. */}
                    <div className="flex items-center justify-center mt-3">
                      <WatchlistSelector />
                    </div>
                  </>
                )}
              </div>
            </>
          );
        })()}
        {/* Feb 2026 — Recent Searches chips row. Shows only when
            (a) the user has opened at least one stock this session
            and (b) the search input is on-screen (All Stocks view,
            not Watchlist/Portfolio). Tapping a chip opens that
            stock's modal instantly via `handleStockClick` (which
            in turn re-pushes it to the front of the list). The X
            button removes just that ticker; a subtle "Clear"
            button at the end nukes the whole row.
            Aug 2026 — On MOBILE this block is hidden here (via
            `hidden sm:flex`) and re-rendered below the mobile
            search input so it sits between search and table, per
            user request. Desktop keeps it right under the search
            form as before. */}
        {recentSearches.length > 0 && viewMode !== 'watchlist' && viewMode !== 'portfolio' && (
          <div
            className="mt-2 hidden sm:flex items-center gap-1.5 flex-wrap"
            data-testid="recent-searches-row"
          >
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground shrink-0" data-testid="recent-searches-label">
              Recent
            </span>
            {recentSearches.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 dark:bg-amber-400/10 pl-2.5 pr-1 py-0.5 text-[11px] font-semibold text-amber-800 dark:text-amber-300 hover:bg-amber-500/20 transition-colors"
                data-testid={`recent-search-chip-${t}`}
              >
                <button
                  type="button"
                  onClick={() => handleStockClick(t)}
                  className="tracking-wide"
                  data-testid={`recent-search-chip-open-${t}`}
                  title={`Reopen ${t}`}
                >
                  {t}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setRecentSearches((prev) => {
                      const next = prev.filter((x) => x !== t);
                      try { localStorage.setItem(RECENT_SEARCH_KEY, JSON.stringify(next)); } catch { /* noop */ }
                      return next;
                    });
                  }}
                  className="p-0.5 rounded-full hover:bg-amber-500/20"
                  data-testid={`recent-search-chip-remove-${t}`}
                  title={`Remove ${t} from Recent`}
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            ))}
            <button
              type="button"
              onClick={() => {
                setRecentSearches([]);
                try { localStorage.removeItem(RECENT_SEARCH_KEY); } catch { /* noop */ }
              }}
              className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2 ml-1"
              data-testid="recent-searches-clear-all"
              title="Clear all recent searches"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Stock Table or Portfolio View */}
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-0">
        {showCalendar ? (
          <DividendCalendar onBack={() => { navigate('/?view=portfolio'); }} isDark={isDark} onStockClick={handleStockClick} />
        ) : viewMode === 'portfolio' ? (
          <>
            {/* Round P140·6 (Feb 2026) — Warrant Exposure summary
                strip. Silent render when the user holds zero warrants;
                otherwise surfaces aggregate cost + market + P/L across
                every warrant + a per-warrant mini-table. */}
            <WarrantExposureCard />
            <PortfolioSummary
              isDark={isDark}
              refreshKey={chartsRefreshKey}
              backSlot={
                <BackOrHomeButton
                  testid="portfolio-back-inline-btn"
                  showEscHint={false}
                  className="shrink-0"
                />
              }
              onWhatIfClick={() => navigate('/whatif')}
              onCompareClick={() => setShowCompareModal(true)}
              onStockClick={handleStockClick}
              onRebalancerClick={() => navigate('/rebalancer')}
              onTaxLotsClick={() => navigate('/tax-lots')}
              onYearEndClick={() => navigate('/year-end-report')}
              onTaxAuditClick={() => navigate('/tax-account-audit')}
              onExportCsv={() => downloadCSV('portfolio/export-csv', 'portfolio_export.csv')}
              onImportCsv={openCsvImportGuarded}
              onDripCalcClick={() => setShowDripModal(true)}
            />
            <PaycheckStrip refreshKey={chartsRefreshKey} onStockClick={handleStockClick} />
            <PaydayPulseCards refreshKey={chartsRefreshKey} onStockClick={handleStockClick} />
            <PaidThisMonthCard refreshKey={chartsRefreshKey} onStockClick={handleStockClick} />
            <IncomeHeatmap
              refreshKey={chartsRefreshKey}
              onCalendarClick={() => navigate('/?view=calendar')}
            />
            {/* Round 95bp (Jun 2026) — DRIP Calculator button moved
                from PersonalStreaksCard to the Monthly Dividend Goal
                card (rendered inside <PortfolioSummary />). */}
            <PersonalStreaksCard
              refreshKey={chartsRefreshKey}
            />
            {/* Round 86 (Feb 2026) — PortfolioCharts (Portfolio Value +
                Sector Allocation) was relocated INTO PortfolioSummary
                so it now renders directly under the Capital Tracker
                card. The Benchmark Comparison stays at the bottom of
                the analytics block. */}
            {/* Round 92o++++++++N++++++++++ (May 2026) — Plus-tier
                per-portfolio dividend contrast. Sits between the
                Streaks block (above, via StreakHighlightRow) and the
                Benchmark Comparison card (below) per user spec. The
                component returns null for free users and for Plus
                users who haven't created any named portfolios yet,
                so this slot is invisible until it's earned. */}
            {/* Sep 2026 — Income by Portfolio card retired. Its
                Conviction Snapshot is now a standalone card and its
                risk banners render under Benchmark Comparison. */}
            <ConvictionSnapshotCard onStockClick={handleStockClick} />

            {/* Benchmark Comparison sits AFTER the sector-allocation
                charts per April-2026 layout request — keeps the visual
                "results" block (value + sector breakdown + benchmarks)
                grouped together. */}
            <BenchmarkComparison
              key={`bench-${chartsRefreshKey}`}
              onCompareClick={() => setShowCompareModal(true)}
            />

            {/* Sep 2026 — per-portfolio income concentration / ADR tax
                drag banners, above the dividend cut/raise banners that
                PortfolioView renders. */}
            <PortfolioRiskBanners onStockClick={handleStockClick} />
            {/* Oct 2026 — above-par callable preferred exposure. */}
            <PortfolioCallExposureBanner onStockClick={handleStockClick} />

            {/* Portfolio Action Icons — May 2026: News, Calendar, DRIP
                Calculator, Rebalancer, What If? and Compare were
                relocated into their context-relevant cards (My Portfolio,
                Income Heatmap, Sector Allocation, Benchmark Comparison,
                Your Dividend Streaks, and the portfolio header bar)
                so the action bar isn't a wall of unrelated icons.
                Feb 2026 — the four CSV/tax-export buttons (Tax Lots,
                Year-End, Export CSV, Import CSV) were moved up into the
                Capital Tracker card, one button per stat tile. This
                action-bar slot is now empty by design; keeping the
                comment so future agents don't accidentally re-add them. */}

            <TaxHarvestBanner />
            <NiitYearEndBanner />
            <NiitOnboardingNudge hasPositions={(ownedTickers?.length || 0) > 0} />
            <BrokerTaggingNudge hasPositions={(ownedTickers?.length || 0) > 0} />

            <PortfolioView
              ownedTickers={ownedTickers}
              stocks={stocks}
              onPositionsChanged={() => { fetchOwnedTickers(); window.dispatchEvent(new Event('portfolio-changed')); }}
              onStockClick={handleStockClick}
              showNews={showNews}
              onNewsToggle={() => setShowNews(prev => !prev)}
            />

            {/* Round P118g (Feb 2026) — Recent Corp Actions panel.
                Renders only when the user has un-undone
                applied_corp_actions rows on the current portfolio;
                otherwise the component returns null so the layout
                stays clean. Sits ABOVE the destructive Reset button
                so a mistaken split can be undone WITHOUT reaching
                for the nuclear option. */}
            {!isAllReadOnly && (
              <div className="mt-6">
                <RecentCorpActionsPanel
                  portfolioId={dashActivePortfolioId}
                  onUndone={() => window.dispatchEvent(new Event('portfolio-changed'))}
                />
              </div>
            )}

            {/* Destructive: Reset & Delete Portfolio — sits ABOVE the site-wide
                disclaimer (that disclaimer lives outside this block). Visible only
                in Portfolio view AND only when NOT on the Plus-tier All view (the
                All view is a summary, not a real portfolio to wipe). */}
            {!isAllReadOnly && (
            <div className="mt-6">
              <button
                onClick={() => setShowResetPortfolioDialog(true)}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg border-2 border-red-500/70 bg-red-50/70 dark:bg-red-950/30 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-950/50 transition-colors text-sm font-bold"
                data-testid="reset-portfolio-btn"
                title="Wipe all positions, cash, transactions, income goal, and your share slug"
              >
                <AlertTriangle className="w-6 h-6 shrink-0" />
                <span>Reset and Delete Portfolio</span>
                <span className="text-xl leading-none" aria-hidden="true">{'\u2620\uFE0F'}</span>
              </button>
            </div>
            )}
          </>
        ) : (
          <>
            {/* Mobile-only: tier legend + Challenge icon, centered ABOVE the blue info text.
                Round 97l (Feb 2026) — Screener link joined this row so
                the top of the mobile main page reads: Screener +
                Challenge + Badge Key. Used to live in the
                `mobile-table-controls-row` below — moved here per UX
                request to keep all "page-level" nav together at the
                top, with table-level controls grouped at the bottom.
                Watchlist hides this — those two controls move to the top
                toolbar row on the watchlist mobile layout. */}
            {viewMode !== 'watchlist' && (
            <div className="sm:hidden flex items-center justify-center gap-2.5 text-xs text-muted-foreground mb-2" data-testid="tier-legend-mobile">
              <Link to="/screener" data-testid="screener-link-mobile-top">
                <button className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors shrink-0 px-1.5 py-1">
                  <Filter className="w-4 h-4 text-primary" />
                  <span>Screener</span>
                </button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors px-1.5 py-1" data-testid="challenge-dropdown-trigger-mobile-legend">
                    <span className="text-base">{'\u{1F451}'}</span>
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center">
                  <Link to="/kings-challenge">
                    <DropdownMenuItem className="cursor-pointer gap-2">
                      <span>{'\u{1F451}'}</span> Kings Challenge
                    </DropdownMenuItem>
                  </Link>
                  <Link to="/aristocrats-challenge">
                    <DropdownMenuItem className="cursor-pointer gap-2">
                      <span>{'\u{1F3C6}'}</span> Aristocrats Challenge
                    </DropdownMenuItem>
                  </Link>
                  <Link to="/achievers-challenge">
                    <DropdownMenuItem className="cursor-pointer gap-2">
                      <span>{'\u{1F396}\uFE0F'}</span> Achievers Challenge
                    </DropdownMenuItem>
                  </Link>
                </DropdownMenuContent>
              </DropdownMenu>
              <BadgeKey />
            </div>
            )}

            {/* Round 97l (Feb 2026) — old watchlist mobile
                `mobile-table-controls-row-watchlist` (Screener +
                Clear Search + Clear Sort) was deleted from this
                slot. Screener moved up to the watchlist top toolbar
                next to the Back arrow. Clear Search / Clear Sort
                already live next to the search input in the
                `mobile-search-row` further below — no reason to
                duplicate them here. Comment kept so future agents
                don't re-add the trio in this slot. */}

            {/* Round 92r — phone-only search/count/LIVE row has been
                relocated to AFTER the "Clicking on a stock updates…"
                info text (see block further below). Removing the
                old above-controls placement here per UX request. */}

            {/* Streak rows are rendered at the TOP of the page for
                viewMode==='all' (see hoisted block near line 555) so
                they sit above the Search / Screener / Challenge /
                Badge Key toolbar per May-2026 layout update. Kept
                here only as a reminder — DO NOT re-add the components
                in this slot unless the hoist is also removed. */}

            {/* Round 97l (Feb 2026) — old `mobile-table-controls-row`
                (Screener + Clear Search + Clear Sort) was deleted from
                this slot. Screener now lives in the top
                `tier-legend-mobile` row, and the Clear Search /
                Clear Sort buttons relocated DOWN into the
                `mobile-search-row` so they sit next to the search box
                they affect. Kept this comment so future agents don't
                accidentally re-add the trio here. */}

            {/* Feb 2026 — Watchlist Snapshot + Blend + Pinned Pairs +
                Conviction Snapshot + Unusual Activity + Hikes/Cuts +
                Ex-Div strips MOVED below the stock table per user
                spec. The toolbar (News / Time Machine / Alerts / LIVE
                / count / Clear Sort) and the table itself now render
                at the top of the Watchlist view so users see their
                actual holdings first. See paired block further down
                in this file (starts with "MOVED Watchlist analytics").

                Round 93r — Watchlist ex-div alert settings panel
                (disclosed by the "Alerts" toolbar button below).
                Round 98o (Jul 2026) — MOVED from above the toolbar
                to right after the toolbar so the panel opens
                DIRECTLY BELOW the Alerts button, matching the
                Calendar / IPO-Watch disclosure pattern the user
                asked for. See paired removal further down. */}

            {/* Info text above stock table (desktop keeps stock count here; mobile has its own above).
                On the Watchlist view we also surface a "News" toggle that
                renders an inline news feed for every ticker in the
                watchlist — same UX as the Portfolio's News button, just
                scoped to the watchlist tickers. */}
            <div className="flex items-center justify-center gap-3 mb-3 flex-wrap">
              {viewMode === 'watchlist' && watchlist.length > 0 && (
                <button
                  onClick={() => setShowWatchlistNews(prev => !prev)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
                    showWatchlistNews
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-950/60'
                  }`}
                  data-testid="watchlist-news-toggle"
                  title="Show recent news for every stock in your watchlist"
                >
                  <Newspaper className="w-3.5 h-3.5" />
                  <span>News</span>
                </button>
              )}
              {viewMode === 'watchlist' && watchlist.length > 0 && (
                <button
                  onClick={() => setShowTimeMachine(prev => !prev)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
                    showTimeMachine
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'bg-orange-50 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-950/60'
                  }`}
                  data-testid="watchlist-time-machine-toggle"
                  title="Show what $1,000 invested in each stock 1 year ago (with DRIP on) would be worth today"
                >
                  <Clock className="w-3.5 h-3.5" />
                  <span>Time Machine</span>
                </button>
              )}
              {/* Round 93r — Watchlist ex-div alert settings entry point.
                  Governs only the watchlist alert toggle (portfolio alert
                  settings live on the Calendar page per user spec). */}
              {viewMode === 'watchlist' && watchlist.length > 0 && (
                <button
                  onClick={() => setShowWatchlistAlertSettings(prev => !prev)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
                    showWatchlistAlertSettings
                      ? 'bg-violet-600 text-white shadow-sm'
                      : 'bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-950/60'
                  }`}
                  data-testid="watchlist-alerts-settings-toggle"
                  title="Configure ex-dividend alert pop-ups for your watchlist"
                >
                  <BellRing className="w-3.5 h-3.5" />
                  <span>Alerts</span>
                </button>
              )}
              {/* Round 98o (Jul 2026) — Portfolio Alerts button.
                  Toggles the ExDivAlertSettings panel below the
                  toolbar. Matches the visual + interaction pattern
                  of the Watchlist Alerts button + the IPO Watch
                  Alerts button. */}
              {/* Round 98o (Jul 2026) — Dashboard-level Portfolio
                  Alerts toggle removed; the alerts pill now lives
                  inside <PortfolioSummary>. The state hook is
                  commented out at line ~272 as reference. The
                  button JSX that referenced setShowPortfolioAlertSettings
                  was accidentally left behind, causing a ReferenceError
                  on portfolio view — removed in Round P108e (Feb 2026)
                  after lint audit surfaced the dangling references. */}
              {/* Round 92r — LIVE pill is desktop/tablet only here.
                  On phones it now lives in the relocated search row
                  below this info text so the "$ search + count + LIVE"
                  cluster sits together. */}
              <div className="hidden sm:flex">
                <LivePriceIndicator status={dashboardStreamStatus} />
              </div>
              <p
                className="text-xs font-medium text-amber-900 dark:text-yellow-400"
                data-testid="stock-info-text"
                hidden={viewMode === 'watchlist'}
              >
                Clicking on a stock updates current data, 15 min. delayed.
              </p>
              <span className="hidden sm:inline text-xs text-muted-foreground" data-testid="stock-count-badge">
                {(() => {
                  if (viewMode === 'watchlist') {
                    const limit = userLimits?.watchlist_limit ?? 10;
                    return `${watchlist.length}/${limit} in watchlist`;
                  }
                  if (searchQuery.trim()) return `${filteredStocks.length} match${filteredStocks.length === 1 ? '' : 'es'}`;
                  return `${totalStocks.toLocaleString()} stocks`;
                })()}
              </span>
              {/* Round 95z — Section 4: 3-way segmented control. Only
                  rendered on the main "All" view (not Watchlist /
                  Portfolio where the dividend/non-div distinction has
                  no relevance). Round 96b (Feb 2026) — stays visible
                  during search so the user can flip filter mode
                  mid-search; backend `/api/stocks/search` now honors
                  `dividend_filter`.
                  Round 96c (Jul 2026) — Watchlist desktop also drops
                  this switch per user request. Users no longer need
                  it because dividend/non-div segmentation is visible
                  in the badge column and the Watchlist stays scoped
                  to a small tracked list anyway. */}
              {viewMode === 'all' && (
                <div className="hidden sm:inline-flex items-center ml-2" data-testid="dividend-filter-wrap-desktop">
                  <DividendFilterSegmentedControl
                    value={dividendFilter}
                    onChange={handleDividendFilterChange}
                    isAdmin={user?.role === 'admin'}
                    instanceId="dashboard-desktop"
                  />
                </div>
              )}
              {/* Desktop-only: Clear Sort lives on the same info-text
                  line, on the right of the stock count. Round 96c
                  (Jul 2026) — Clear Search button removed globally on
                  desktop per user request; the search box already has
                  a native clear affordance when focused, and the button
                  was cluttering the toolbar. */}
              {viewMode !== 'portfolio' && viewMode !== 'watchlist' && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  disabled={!isSearchActive}
                  className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition-colors bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-muted/60 disabled:hover:text-muted-foreground"
                  data-testid="clear-search-btn"
                  title="Clear the search box and return the table to its default state"
                >
                  <X className="w-3 h-3" />
                  Clear Search
                </button>
              )}
              <button
                type="button"
                onClick={handleClearSort}
                disabled={isSortDefault}
                className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition-colors bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-muted/60 disabled:hover:text-muted-foreground"
                data-testid="clear-sort-btn"
                title="Reset the column sort back to its default (name, ascending)"
              >
                <X className="w-3 h-3" />
                Clear Sort
              </button>
            </div>

            {/* Round 98o (Jul 2026) — Alerts settings disclosure panels
                for Watchlist + Portfolio views. Placed directly BELOW
                the toolbar row above (which hosts the Alerts pill
                buttons) so the panel opens under the button — matching
                the Calendar / IPO-Watch disclosure pattern. */}
            {viewMode === 'watchlist' && watchlist.length > 0 && showWatchlistAlertSettings && (
              <div className="mt-3 rounded-xl border-2 border-violet-500/30 bg-card p-4 max-w-2xl" data-testid="watchlist-alerts-settings-panel">
                <ExDivAlertSettings
                  showWatchlistOnly={true}
                  onClose={() => setShowWatchlistAlertSettings(false)}
                  onStockClick={(ticker) => { setShowWatchlistAlertSettings(false); handleStockClick(ticker); }}
                >
                  <div className="border-t border-border pt-4">
                    <Stage2AlertSettings onStockClick={(ticker) => { setShowWatchlistAlertSettings(false); handleStockClick(ticker); }} />
                  </div>
                </ExDivAlertSettings>
              </div>
            )}
            {/* Round 98o — Portfolio panel lives inside
                <PortfolioSummary> now (moved to render above
                <MacroYieldStrip> per user request). No panel here. */}

            {/* Round 97l (Feb 2026) — Phone-only layout, top→bottom:
                  Row B = 3-way segmented control + LIVE pill
                  Row A = Search input + stock count + Clear Search +
                          Clear Sort
                Sits BELOW the "Clicking on a stock updates…" info
                text so reading order is:
                  tip → filter+live → search+controls → table.
                Desktop/tablet keep the original single-row layout.
                Round 96c (Jul 2026) — Watchlist mobile no longer
                shows the 3-way switch or search input; it renders a
                bespoke row with Badge Key + count + Clear Sort. */}
            {/* Row B — 3-way switch + LIVE (all view only; watchlist
                dropped per user request Round 96c). Round 99c (Jul 6,
                2026) — also drop the LIVE pill on mobile watchlist per
                user request; the whole row now hides on watchlist view
                to avoid an empty `mb-2` gap. */}
            {viewMode !== 'watchlist' && (
              <div className="sm:hidden flex items-center justify-center gap-2 mb-2" data-testid="mobile-filter-live-row">
                {viewMode === 'all' && (
                  <div data-testid="dividend-filter-wrap-mobile">
                    <DividendFilterSegmentedControl
                      value={dividendFilter}
                      onChange={handleDividendFilterChange}
                      isAdmin={user?.role === 'admin'}
                      instanceId="dashboard-mobile"
                    />
                  </div>
                )}
                <LivePriceIndicator status={dashboardStreamStatus} />
              </div>
            )}
            {/* Row A — Watchlist mobile: Badge Key + count. Feb 2026 —
                Clear Sort moved from this row to the LEFT side of the
                yield-mode toggle bar inside StockTable (mobile only)
                per user spec, so the row above the NAME header now
                carries Clear Sort ← ... → Nominal / Real. Count text
                also promoted to bold + "in watchlist" suffix. */}
            {viewMode === 'watchlist' && (
              <div className="sm:hidden flex items-center justify-center gap-2 mb-3" data-testid="mobile-watchlist-controls-row">
                <BadgeKey />
                <span className="text-xs font-bold text-foreground whitespace-nowrap shrink-0" data-testid="stock-count-badge-mobile">
                  {(() => {
                    const limit = userLimits?.watchlist_limit ?? 10;
                    return `${watchlist.length}/${limit} in watchlist`;
                  })()}
                </span>
              </div>
            )}
            {/* Row A — All view mobile: Search + count + Clear Sort
                (Clear Search dropped Round 96c; search box has its
                own native clear affordance). */}
            {viewMode !== 'watchlist' && viewMode !== 'portfolio' && (
              <div className="sm:hidden flex items-center gap-2 mb-3" data-testid="mobile-search-row">
                <div className="relative flex-1 min-w-0">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="search-input-mobile"
                    type="text"
                    placeholder="Search…"
                    value={searchQuery}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (viewMode !== 'all' || showCalendar) {
                        setViewMode('all');
                        setShowCalendar(false);
                        setShowNews(false);
                        if (location.search) navigate('/', { replace: true });
                      }
                      setSearchQuery(v);
                    }}
                    className="pl-9 h-9 text-sm"
                  />
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0" data-testid="stock-count-badge-mobile">
                  {(() => {
                    if (searchQuery.trim()) return `${filteredStocks.length} match${filteredStocks.length === 1 ? '' : 'es'}`;
                    return `${totalStocks.toLocaleString()} stocks`;
                  })()}
                </span>
                <button
                  type="button"
                  onClick={handleClearSort}
                  disabled={isSortDefault}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold transition-colors bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-muted/60 disabled:hover:text-muted-foreground shrink-0"
                  data-testid="clear-sort-btn-mobile"
                  title="Reset the column sort back to its default (name, ascending)"
                >
                  <X className="w-3 h-3" />
                  Clear Sort
                </button>
              </div>
            )}
            {/* Aug 2026 — Recent Searches (MOBILE). Sits directly
                between the mobile search input and the stock table
                so it's visible above the fold on phones. The
                desktop version above stays hidden on mobile via
                `hidden sm:flex`. */}
            {recentSearches.length > 0 && viewMode !== 'watchlist' && viewMode !== 'portfolio' && (
              <div
                className="sm:hidden mb-3 flex items-center gap-1.5 flex-wrap"
                data-testid="recent-searches-row-mobile"
              >
                <span className="text-[11px] uppercase tracking-wide text-muted-foreground shrink-0">
                  Recent
                </span>
                {recentSearches.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 dark:bg-amber-400/10 pl-2.5 pr-1 py-0.5 text-[11px] font-semibold text-amber-800 dark:text-amber-300"
                    data-testid={`recent-search-chip-mobile-${t}`}
                  >
                    <button
                      type="button"
                      onClick={() => handleStockClick(t)}
                      className="tracking-wide"
                      data-testid={`recent-search-chip-mobile-open-${t}`}
                      title={`Reopen ${t}`}
                    >
                      {t}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setRecentSearches((prev) => {
                          const next = prev.filter((x) => x !== t);
                          try { localStorage.setItem(RECENT_SEARCH_KEY, JSON.stringify(next)); } catch { /* noop */ }
                          return next;
                        });
                      }}
                      className="p-0.5 rounded-full hover:bg-amber-500/20"
                      data-testid={`recent-search-chip-mobile-remove-${t}`}
                      title={`Remove ${t} from Recent`}
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setRecentSearches([]);
                    try { localStorage.removeItem(RECENT_SEARCH_KEY); } catch { /* noop */ }
                  }}
                  className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2 ml-1"
                  data-testid="recent-searches-clear-all-mobile"
                  title="Clear all recent searches"
                >
                  Clear
                </button>
              </div>
            )}
            {viewMode === 'watchlist' && showWatchlistNews && watchlist.length === 0 && (
              <WatchlistNews tickers={[]} />
            )}
            {/* Round P136 (Feb 2026) — Watchlist leverage concentration
                nudge. Amber banner appears above the watchlist table
                when > 30% of tracked tickers are in the curated
                structurally-leveraged CEF set. Uses the shared
                `useLeveragedCefs` hook so no extra fetch. Mirrors the
                Portfolio banner semantically but is scoped to the
                watchlist so heavy-yield chasers see the concentration
                warning while researching, not just after they buy. */}
            {viewMode === 'watchlist' && watchlist.length > 0 && (
              <WatchlistLeverageCallout watchlist={watchlist} stocks={stocks} />
            )}
            {/* Slice search results into 25-per-page chunks. The
                `searchPage` index is reset to 1 every time the query
                changes (see the search useEffect above). */}
            <StockTable
              stocks={(() => {
                // Round 97d (Feb 27, 2026) — Watchlist client-side
                // dividend filter. Watchlist tickers are fetched in
                // bulk via /stocks/batch (no filter param), so we
                // apply the 3-way pill choice on the rendered slice.
                //
                // Round 96c (Jul 2026) — Watchlist ALWAYS shows every
                // ticker the user tracks (dividend + non-dividend)
                // now that the 3-way switch was removed from the
                // Watchlist toolbar. Users add whatever they want to
                // their watchlist; the UI shouldn't secretly hide
                // half the list because of a lingering filter
                // preference from the All Stocks view.
                //
                // Round 96i (Jul 2026) — Client-side sort for
                // Watchlist / Portfolio views.
                //
                // Round 102m2 (Feb 2026) — Sort MUST happen against
                // the FULL filtered result set BEFORE slicing to
                // the current page. Previously we sliced page-first
                // and sorted the 25-row page, which produced the
                // "each page sorts individually" bug — the user
                // could see AAPL#12 rank above AA#500 because the
                // sort only saw one page at a time. Screener does
                // this the same way (sort → slice) which is why
                // its sort works across all filter results.
                const sortRowsClientSide = (rows) => {
                  if (!rows || rows.length === 0) return rows;
                  // Round 102m5 (Feb 2026) — PAYOUT column shows the
                  // dividend `frequency` (Monthly / Quarterly /
                  // Semi-Annually / Annually), NOT a numeric payout
                  // ratio. Sort by cadence rank (Weekly=1 → Irregular=8)
                  // so clicking PAYOUT ▲ groups Weekly → Monthly →
                  // Quarterly → etc. Previously mapped to payout_ratio
                  // which just produced a random-looking order because
                  // most non-payers had null and the numeric column
                  // isn't the one being displayed.
                  const FREQ_RANK = {
                    'Weekly': 1, 'Bi-Weekly': 2, 'Monthly': 3,
                    'Bi-Monthly': 4, 'Quarterly': 5, 'Semi-Annually': 6,
                    'Semi-Annual': 6, 'Annually': 7, 'Annual': 7,
                    'Irregular': 8, 'Special': 9,
                  };
                  const keyMap = {
                    name: (s) => (s?.name || s?.ticker || '').toLowerCase(),
                    price: (s) => Number(s?.price ?? -Infinity),
                    market_cap: (s) => Number(s?.market_cap ?? -Infinity),
                    yield: (s) => Number(s?.dividend_yield ?? -Infinity),
                    divyr: (s) => Number(s?.dividend_rate ?? -Infinity),
                    payout: (s) => FREQ_RANK[s?.frequency] ?? 99,
                    sector: (s) => (s?.sector || '').toLowerCase(),
                    industry: (s) => (s?.industry || '').toLowerCase(),
                    rev_cagr: (s) => Number(s?.revenue_cagr_3y ?? s?.rev_cagr ?? -Infinity),
                    '1mo': (s) => Number(s?.perf_1m ?? s?.performance_1m ?? -Infinity),
                    '3mo': (s) => Number(s?.perf_3m ?? -Infinity),
                    '6mo': (s) => Number(s?.perf_6m ?? s?.performance_6m ?? -Infinity),
                    '1yr': (s) => Number(s?.perf_1y ?? s?.performance_1y ?? -Infinity),
                    // Round P143 (Feb 2026) — Ex-Div sort. Backend
                    // stores `next_ex_date` as a human string
                    // ("Aug 24, 2026") which does NOT sort correctly
                    // lexicographically (Aug < Dec < May alphabetically).
                    // Parse to epoch ms so numeric compare orders by
                    // real calendar date. Nulls / unparsable values
                    // fall to the end regardless of direction.
                    exdiv: (s) => {
                      const raw = s?.next_ex_date || s?.next_ex_dividend_date;
                      if (!raw) return Number.POSITIVE_INFINITY;
                      const t = Date.parse(raw);
                      return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
                    },
                  };
                  const getVal = keyMap[sortKey] || keyMap.name;
                  const dirMul = sortDir === 'desc' ? -1 : 1;
                  return [...rows].sort((a, b) => {
                    const va = getVal(a);
                    const vb = getVal(b);
                    if (typeof va === 'string' && typeof vb === 'string') {
                      return va.localeCompare(vb) * dirMul;
                    }
                    const aNaN = !isFinite(va);
                    const bNaN = !isFinite(vb);
                    if (aNaN && bNaN) return 0;
                    if (aNaN) return 1;
                    if (bNaN) return -1;
                    return (va - vb) * dirMul;
                  });
                };

                // Search-active in All view: sort the FULL 311 (or
                // however many) filteredStocks first, THEN paginate
                // to the current 25-row window. Any dividendFilter
                // still applies before sorting.
                if (viewMode === 'all' && searchQuery.trim()) {
                  let full = filteredStocks;
                  // Round 95p·13 — Match isValidSearchRow's REIT/BDC
                  // pre-declaration exception at render time too.
                  // Without this the render pass would re-exclude
                  // BXDC/CEPS/YICCU-type rows even though the search
                  // effect correctly kept them in filteredStocks.
                  // Round P140·3 (Feb 2026) — Same treatment for
                  // is_warrant rows: keep them under `dividend` +
                  // `all` filters (matches isValidSearchRow), suppress
                  // them from `non_dividend` browse.
                  const isPending = (s) => !!(s?.is_reit || s?.is_bdc) && s?.pays_dividend !== true;
                  const isWarrant = (s) => !!s?.is_warrant;
                  if (dividendFilter === 'non_dividend') {
                    full = full.filter(s => s?.pays_dividend === false && !isPending(s) && !isWarrant(s));
                  } else if (dividendFilter === 'dividend') {
                    full = full.filter(s => s?.pays_dividend !== false || isPending(s) || isWarrant(s));
                  }
                  // Preserve the backend's relevance ranking (exact
                  // ticker match first — e.g. "KO" → Coca-Cola on top)
                  // while the sort is still the default name ▲. Only
                  // re-sort client-side when the user explicitly
                  // clicked a column. Mirrors the Screener's
                  // _is_default_sort behavior.
                  const isDefaultSort = sortKey === 'name' && sortDir === 'asc';
                  const sorted = isDefaultSort ? full : sortRowsClientSide(full);
                  return sorted.slice((searchPage - 1) * 25, searchPage * 25);
                }

                if (viewMode === 'watchlist' || viewMode === 'portfolio') {
                  return sortRowsClientSide(filteredStocks);
                }

                const baseStocks = filteredStocks;
                if (viewMode !== 'watchlist' || dividendFilter === 'all') return baseStocks;
                const isPendingWL = (s) => !!(s?.is_reit || s?.is_bdc) && s?.pays_dividend !== true;
                if (dividendFilter === 'non_dividend') {
                  return baseStocks.filter(s => s?.pays_dividend === false && !isPendingWL(s));
                }
                // 'dividend' (default) — hide explicit non-div rows; keep
                // unknown (pays_dividend undefined) AND pre-declaration
                // REIT/BDC rows which are dividend-bearing structurally.
                return baseStocks.filter(s => s?.pays_dividend !== false || isPendingWL(s));
              })()}
              loading={loading || viewLoading}
              onStockClick={handleStockClick}
              onPerfClick={handlePerfClick}
              watchlist={watchlist}
              ownedTickers={ownedTickers}
              onToggleWatchlist={toggleWatchlist}
              user={user}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={handleSort}
              onClearSort={viewMode === 'watchlist' ? handleClearSort : undefined}
              isSortDefault={isSortDefault}
              showRadarChips={viewMode === 'watchlist'}
              trendingTickers={trendingWatchlistTickers}
              isWatchlistView={viewMode === 'watchlist' || viewMode === 'portfolio'}
              dividendFilter={viewMode === 'all' ? dividendFilter : (viewMode === 'watchlist' ? 'all' : 'dividend')}
              allowSpacsWarrants={allowSpacsWarrants}
              onAllowSpacsWarrantsChange={setAllowSpacsWarrants}
              allowMmf={allowMmf}
              onAllowMmfChange={setAllowMmf}
              emptyMessage={viewMode === 'watchlist' ? {
                title: 'Your watchlist is empty',
                hint: 'Add stocks to your watchlist by clicking the Star icon in front of a stock in any stock search.',
              } : undefined}
              interleaveAfterRow={
                (viewMode === 'watchlist' || viewMode === 'portfolio')
                  ? (ticker) => {
                      // Round 97d3 (Feb 27, 2026) — Hide Time Machine for
                      // non-dividend watchlist rows. DRIP simulation is
                      // only meaningful for dividend payers. The watchlist
                      // rows come from /stocks/batch (filteredStocks), NOT
                      // the default-page `stocks` universe — so look up
                      // pays_dividend there. The previous lookup against
                      // `stocks` silently returned undefined for tickers
                      // outside the default page (TSLA/NPAC), so the
                      // non-div check fell through and the bar rendered.
                      const stockRow = filteredStocks.find(s => s.ticker === ticker)
                        || stocks.find(s => s.ticker === ticker);
                      const isNonDivRow = stockRow && stockRow.pays_dividend === false;
                      const isWatchlist = viewMode === 'watchlist';
                      return (
                      <div className="space-y-1">
                        {/* Time Machine + ticker news are watchlist-only
                            features — the portfolio view has its own
                            richer P/L widgets. */}
                        {isWatchlist && showTimeMachine && !isNonDivRow && <WatchlistTimeMachineBar ticker={ticker} />}
                        {isWatchlist && showWatchlistNews && (
                          <div className="px-3 sm:px-6 pt-1 pb-3">
                            <TickerNewsBlock ticker={ticker} />
                          </div>
                        )}
                        {/* Round P141/P142·3 (Feb 2026) — Warrant alert
                            strip. Fires when the row is a warrant/unit
                            AND any of: redemption 8-K filed, force-
                            redeemed, within 30 days of expiry, OR
                            underlying within 5% of exercise price.
                            Silent on rows that don't match. Per privacy
                            policy this is on-site only — no email.
                            Round P142·4 — Now also renders on the
                            PORTFOLIO view (not just the Watchlist)
                            since most users drop a ticker from the
                            watchlist once they buy it. */}
                        <WarrantAlertStrip stock={stockRow} />
                        {/* Starred Filings — visible on both watchlist
                            and portfolio views (previously watchlist-
                            only). Returns null when the user has no
                            starred filings for this ticker. */}
                        <StarredFilingsStrip ticker={ticker} />
                      </div>
                      );
                    }
                  : undefined
              }
            />

            {/* Feb 2026 — MOVED Watchlist analytics: Snapshot + Blend +
                Pinned Pairs + Conviction Snapshot + Unusual Activity +
                Hikes/Cuts + Ex-Div. Per user request these all now sit
                BELOW the table so the actual holdings render first.
                Feb 2026 (later same session) — Unusual Activity + Ex-Div
                This Week now render BEFORE the Watchlist Snapshot so
                the two time-sensitive strips are the first thing under
                the holdings table. */}
            {viewMode === 'watchlist' && filteredStocks.length > 0 && (
              <UnusualActivityLine
                stocks={filteredStocks}
                onStockClick={handleStockClick}
              />
            )}
            {viewMode === 'watchlist' && filteredStocks.length > 0 && (
              <ExDividendThisWeekLine
                stocks={filteredStocks}
                onStockClick={handleStockClick}
              />
            )}
            {viewMode === 'watchlist' && filteredStocks.length > 0 && (
              <RecentDividendEventsLine
                stocks={filteredStocks}
                onStockClick={handleStockClick}
              />
            )}
            {viewMode === 'watchlist' && watchlist.length > 0 && (
              <div className="mt-6" data-testid="watchlist-coverage-radars">
                {/* Round P126 (Feb 2026) — Coverage Radar cards.
                    Recovery first, then Cut-Risk, each in its own
                    full-width row so they match the Watchlist
                    Snapshot card sitting right below. Moved from
                    the leaderboard page per user request. */}
                <CoverageRadarStrip onOpenStock={handleStockClick} stacked />
              </div>
            )}
            {viewMode === 'watchlist' && watchlist.length > 0 && (
              <div className="mt-6">
                <WatchlistSummaryCard
                  stocks={filteredStocks}
                  onStockClick={handleStockClick}
                  onCompareClick={() => setShowCompareQuality(true)}
                />
              </div>
            )}
            {viewMode === 'watchlist' && watchlist.length > 0 && (
              <div className="mt-4">
                <PortfolioBlendCompareCard
                  user={user}
                  ownedTickers={ownedTickers}
                  watchlistTickers={watchlist}
                  onOpenStock={handleStockClick}
                />
              </div>
            )}
            {viewMode === 'watchlist' && (
              <PinnedPairsRow
                user={user}
                onOpenPair={(a, b) => {
                  setCompareInitialTicker(a);
                  setCompareSnapshot({ a, b });
                  setShowCompareModal(true);
                }}
              />
            )}
            {viewMode === 'watchlist' && watchlist.length > 0 && watchlistHealthSnapshot && (
              <div className="mt-3" data-testid="watchlist-health-snapshot-wrapper">
                <PortfolioHealthSnapshot
                  snapshot={watchlistHealthSnapshot}
                  onStockClick={handleStockClick}
                  testIdPrefix="watchlist"
                  hasPlusAccess={user?.role === 'admin'}
                />
              </div>
            )}

            {viewMode === 'watchlist' && watchlist.length > 0 && (
              <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
                <button
                  onClick={() => setShowClearWatchlistDialog(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/30 transition-all"
                  data-testid="watchlist-clear-btn"
                  title="Remove every stock from your watchlist"
                >
                  <span className="text-sm" aria-hidden="true">{'\u{1F91A}'}</span>
                  <Eraser className="w-3.5 h-3.5" />
                  Clear Watchlist
                </button>
                <button
                  onClick={() => downloadCSV('watchlist/export-csv', 'watchlist_export.csv')}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
                  data-testid="watchlist-csv-export-btn"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export Watchlist CSV
                </button>
              </div>
            )}
            {totalPages > 1 && viewMode === 'all' && !searchQuery.trim() && (
              <div className="flex items-center justify-center gap-2 mt-6" data-testid="pagination">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => { setPage(p => p - 1); scrollToTableTop('[data-search-input="dashboard-primary"]'); }} data-testid="prev-page-btn">Previous</Button>
                <span className="text-sm text-muted-foreground px-3">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => { setPage(p => p + 1); scrollToTableTop('[data-search-input="dashboard-primary"]'); }} data-testid="next-page-btn">Next</Button>
              </div>
            )}
            {/* Search-result pagination — 25 per page, client-side
                slicing of the full /stocks/search response. Hidden when
                fewer than 26 matches so it doesn't appear unnecessarily. */}
            {searchQuery.trim() && viewMode === 'all' && filteredStocks.length > 25 && (() => {
              const searchTotalPages = Math.ceil(filteredStocks.length / 25);
              return (
                <div className="flex items-center justify-center gap-2 mt-6" data-testid="search-pagination">
                  <Button variant="outline" size="sm" disabled={searchPage <= 1} onClick={() => { setSearchPage(p => Math.max(1, p - 1)); scrollToTableTop('[data-search-input="dashboard-primary"]'); }} data-testid="search-prev-page-btn">Previous</Button>
                  <span className="text-sm text-muted-foreground px-3">Page {searchPage} of {searchTotalPages}</span>
                  <Button variant="outline" size="sm" disabled={searchPage >= searchTotalPages} onClick={() => { setSearchPage(p => Math.min(searchTotalPages, p + 1)); scrollToTableTop('[data-search-input="dashboard-primary"]'); }} data-testid="search-next-page-btn">Next</Button>
                </div>
              );
            })()}
          </>
        )}
      </div>

      {/* Disclaimer */}
      <SiteDisclaimer />

      {/* Stock Detail Modal */}
      <StockModal
        stock={selectedStock}
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedStock(null);
          setPrefetchedNews(null);
          setHighlightFiling(null);
          // Closing StockModal with X consumes the Compare snapshot —
          // next time the user opens a stock modal it shouldn't show
          // the stale "← Back to Compare" button.
          setCompareSnapshot(null);
          // Round 96n (Jul 3 2026) — clear the modal-state
          // sessionStorage cache so a later refresh doesn't magically
          // reopen a modal the user just intentionally dismissed.
          try { sessionStorage.removeItem('dividrip:modal-state'); } catch { /* ignore */ }
          // Re-apply current view (portfolio/watchlist/all) after modal close
          const view = new URLSearchParams(location.search).get('view') || '';
          fetchStocks();
          if (view === 'portfolio') fetchOwnedTickers();
          if (view === 'watchlist') fetchWatchlist();
        }}
        loading={loadingDetail}
        isInWatchlist={selectedStock ? watchlist.includes(selectedStock.ticker) : false}
        onToggleWatchlist={toggleWatchlist}
        isDark={isDark}
        prefetchedNews={prefetchedNews}
        user={user}
        onLoginRequired={() => setLoginModalOpen(true)}
        onPositionsChanged={() => { fetchOwnedTickers(); window.dispatchEvent(new Event('portfolio-changed')); }}
        onViewPortfolio={() => {
          setModalOpen(false);
          setSelectedStock(null);
          // Round P108ae (Feb 2026) — clear the modal-restore cache
          // so Dashboard doesn't auto-reopen the same modal on the
          // Portfolio page (the user just intentionally left it via
          // the "View Portfolio" button, not a refresh).
          try { sessionStorage.removeItem('dividrip:modal-state'); } catch { /* ignore */ }
          fetchStocks();
          fetchOwnedTickers();
          showPortfolio();
        }}
        initialTab={initialTab}
        initialChartPeriod={initialChartPeriod}
        highlightFiling={highlightFiling}
        onCompareClick={(ticker) => {
          // Round 95bu (Jun 2026) — leave the Stock Modal mounted
          // underneath so closing Compare returns the user exactly
          // where they were. Previously this closed the Stock Modal
          // first, leaving an empty Portfolio page once Compare was
          // dismissed.
          setCompareInitialTicker(ticker);
          setShowCompareModal(true);
        }}
        onSelectStock={handleStockClick}
        watchlist={watchlist}
        onBackToCompare={
          compareSnapshot
            ? () => {
                // Close StockModal, restore Compare with both A and B
                // re-locked exactly as they were before the user
                // clicked into a single ticker. Slot order is preserved:
                // snapshot.a → slot A, snapshot.b → slot B, regardless
                // of which side the user originally clicked from.
                setModalOpen(false);
                setSelectedStock(null);
                setPrefetchedNews(null);
                setCompareInitialTicker(compareSnapshot.a || null);
                setShowCompareModal(true);
              }
            : null
        }
      />

      <Dialog open={emptyPortfolioModal} onOpenChange={setEmptyPortfolioModal}>
        <DialogContent className="sm:max-w-sm" data-testid="empty-portfolio-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5 text-muted-foreground" /> My Portfolio</DialogTitle>
            <DialogDescription>Click on a stock, go to the &ldquo;My Portfolio&rdquo; tab, and add a position to start tracking.</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      {/* Clear Watchlist confirmation */}
      <Dialog open={showClearWatchlistDialog} onOpenChange={setShowClearWatchlistDialog}>
        <DialogContent className="sm:max-w-sm" data-testid="clear-watchlist-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-lg" aria-hidden="true">{'\u{1F91A}'}</span>
              <Eraser className="w-5 h-5 text-red-500" />
              Clear Watchlist
            </DialogTitle>
            <DialogDescription>
              This will remove all {watchlist.length} {watchlist.length === 1 ? 'stock' : 'stocks'} from your watchlist.
              Your portfolio positions will be unaffected. This can&rsquo;t be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => setShowClearWatchlistDialog(false)}
              disabled={clearingWatchlist}
              data-testid="clear-watchlist-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={clearWatchlist}
              disabled={clearingWatchlist}
              data-testid="clear-watchlist-confirm"
            >
              {clearingWatchlist ? 'Clearing…' : `Yes, clear all ${watchlist.length}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset & Delete Portfolio confirmation
          Round P118 (Feb 2026) — Names the target portfolio explicitly.
          The old copy said "your portfolio" which was ambiguous when
          the user had multiple portfolios; they assumed it meant only
          the one they were viewing when in fact the button was hitting
          the global-wipe endpoint. */}
      <Dialog open={showResetPortfolioDialog} onOpenChange={setShowResetPortfolioDialog}>
        <DialogContent className="sm:max-w-md" data-testid="reset-portfolio-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Reset {resetTargetPortfolioName ? `"${resetTargetPortfolioName}"` : 'Portfolio'}
              <span className="text-lg leading-none" aria-hidden="true">{'\u2620\uFE0F'}</span>
            </DialogTitle>
            <DialogDescription className="space-y-1.5 pt-1">
              <span className="block">
                {resetTargetPortfolioName
                  ? <>This will permanently delete every piece of <span className="font-bold text-red-700 dark:text-red-400">&quot;{resetTargetPortfolioName}&quot;</span> only:</>
                  : <>This will permanently delete every piece of your portfolio:</>}
              </span>
              <span className="block text-foreground font-medium">
                • All stock positions (manual, DRIP, and CSV-imported lots)<br />
                • Cash balance + every transaction (deposits, withdraws, buys, sells, dividends, tax-harvests)<br />
                • Tax-Lot Optimizer, Year-End Snapshot, and Tax-Harvest Report data<br />
                • DRIP Calculator overrides<br />
                {!resetTargetPortfolioName && <>• Monthly Income Goal settings<br />• Your Public Portfolio Share page<br />• Dismissed dividend-payout reminders</>}
              </span>
              {resetTargetPortfolioName && (
                <span className="block pt-1.5 text-emerald-700 dark:text-emerald-400 text-xs font-semibold">
                  Your other portfolios are NOT touched. Only &quot;{resetTargetPortfolioName}&quot; is affected.
                </span>
              )}
              <span className="block pt-1.5 text-muted-foreground text-xs">
                Watchlist, Journal, alerts, Compare history, What-If saves, and your account itself are <span className="font-semibold text-foreground">not</span> touched.
              </span>
              <span className="block pt-1 text-muted-foreground text-xs">
                A 15-minute Undo toast will appear after the reset so you can recover with one click if this was a mistake.
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setShowResetPortfolioDialog(false)} disabled={resettingPortfolio} data-testid="reset-portfolio-cancel">
              Cancel
            </Button>
            <Button variant="destructive" onClick={resetPortfolio} disabled={resettingPortfolio} data-testid="reset-portfolio-confirm">
              {resettingPortfolio ? 'Deleting…' : 'Yes, delete everything'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <LoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} />

      {/* Portfolio DRIP Calculator Modal */}
      <PortfolioDripModal
        open={showDripModal}
        onClose={() => setShowDripModal(false)}
        isDark={isDark}
      />

      {/* CSV Import Modal (P3) — Round 92o++++++++N++++++++++ (May
          2026): explicit `portfolioId` so the dupe-check + lot-tag
          scope to the currently-selected portfolio. Banner inside
          the modal shows the user which portfolio they're importing
          into.
          Feb 2026 (P158·4): also forward the portfolio NAME so the
          "Importing into:" banner shows the real portfolio name
          ("TROY 1") instead of the misleading default placeholder
          ("New portfolio"). Uses the same store snapshot the reset
          dialog reads at line ~423. */}
      <CsvImportModal
        open={showCsvImportModal}
        onOpenChange={setShowCsvImportModal}
        portfolioId={dashActivePortfolioId}
        portfolioName={(() => {
          if (!dashActivePortfolioId) return '';
          const snap = getPortfolioStoreSnapshot();
          const match = (snap?.portfolios || []).find((p) => p.portfolio_id === dashActivePortfolioId);
          return match?.name || '';
        })()}
      />

      {/* Round P108f (Feb 2026) — Guard modal for the "All Portfolios
          + Import CSV" scenario. When the user has zero named
          portfolios and clicks Import CSV, we redirect them here to
          name a portfolio first. CreatePortfolioModal's built-in
          "how" step then hands off to the CSV importer with the
          fresh portfolio_id plumbed in — so the import lands into
          a real target and undo scope-binding works correctly. */}
      <CreatePortfolioModal
        open={showCreatePortfolioForCsv}
        onClose={() => setShowCreatePortfolioForCsv(false)}
      />

      {/* Round 71m: dividend-cut journal prompt — opens when user
          clicks "Journal it" on the cut-alert toast. */}
      {cutAlertEntry && (
        <JournalEntryModal
          prefill={cutAlertEntry}
          onClose={() => setCutAlertEntry(null)}
          onSaved={() => setCutAlertEntry(null)}
        />
      )}

      {/* Side-by-Side Compare Modal */}
      <CompareModal
        open={showCompareModal}
        onClose={() => {
          setShowCompareModal(false);
          setCompareInitialTicker(null);
          // The snapshot is only valid for "StockModal opened FROM
          // Compare". Closing Compare via X/ESC ends that flow.
          setCompareSnapshot(null);
          // Strip ?compare=... from the URL so reloads don't re-open it.
          const params = new URLSearchParams(location.search);
          if (params.has('compare')) {
            params.delete('compare');
            const qs = params.toString();
            navigate(qs ? `/?${qs}` : '/', { replace: true });
          }
        }}
        isDark={isDark}
        initialTickerA={compareInitialTicker}
        initialTickerB={compareSnapshot?.b && compareSnapshot.b !== compareInitialTicker ? compareSnapshot.b : undefined}
        watchlist={watchlist}
        onToggleWatchlist={toggleWatchlist}
        user={user}
        onLoginRequired={() => setLoginModalOpen(true)}
        onOpenStockModal={(ticker, snapshot) => {
          // Save Compare's A/B snapshot so the user can return to
          // exactly the same comparison from the Stock modal header.
          setCompareSnapshot(snapshot && (snapshot.a || snapshot.b) ? snapshot : null);
          // Close Compare, then open the full StockModal for this ticker.
          setShowCompareModal(false);
          setCompareInitialTicker(null);
          // Strip the ?compare=... query param so reload works correctly.
          const params = new URLSearchParams(location.search);
          if (params.has('compare')) {
            params.delete('compare');
            const qs = params.toString();
            navigate(qs ? `/?${qs}` : '/', { replace: true });
          }
          handleStockClick(ticker);
        }}
      />

      {/* Share Portfolio Modal */}
      <SharePortfolioModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
      />

      {/* Tool Box — single launcher for every portfolio-page tool. */}
      <ToolBoxModal
        open={showToolbox}
        onClose={() => setShowToolbox(false)}
        onAction={handleToolboxAction}
      />

      {/* Round 95bs — Basic Calculator moved to a global App-level
          instance (GlobalBasicCalculator) so it's available on every
          page, not just here. Dispatch `open-basic-calculator` to
          open it from anywhere. */}

      {/* Compare Quality Modal — Watchlist side-by-side breakdown */}
      <CompareQualityModal
        open={showCompareQuality}
        onClose={() => setShowCompareQuality(false)}
        watchlistStocks={filteredStocks}
        onStockClick={(ticker) => {
          setShowCompareQuality(false);
          handleStockClick(ticker);
        }}
      />

      {/* Compare Theses Modal — LLM Bull/Bear thesis side-by-side */}
      {showCompareTheses && (
        <CompareThesesModal onClose={() => setShowCompareTheses(false)} />
      )}

      {/* Round 93r — Ex-Div Alert pop-up. Fires once per day (ET) on
          first dashboard visit. Defaults: portfolio_enabled=True,
          windows=[1]. Configurable on the Calendar page. */}
      {user && <ExDivAlertModal onStockClick={handleStockClick} />}
      {user && viewMode === 'portfolio' && <DividendPaymentAlertModal onStockClick={handleStockClick} />}
      {/* Round 98n (Jul 2026) — IPO Alert popup. Fires once per day
          (ET) on Dashboard/Portfolio entry when the user has any new
          IPOs matching their /ipo-watch settings. Server enforces the
          per-day gate via `popup_dismissed_until`. */}
      {user && viewMode === 'portfolio' && <IpoAlertModal />}

      {/* Alerts Panel */}
      <AlertsPanel
        open={showAlerts}
        onClose={() => {
          setShowAlerts(false);
          // Refresh alert counts on close
          if (user) {
            axios.get(`${API}/alerts`, { withCredentials: true })
              .then(res => {
                const alerts = res.data || [];
                setAlertsCount(alerts.length);
                setAlertsTriggered(alerts.filter(a => a.triggered).length);
              }).catch(() => {});
          }
        }}
      />
    </div>
  );
}
